import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Progress } from './components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';

// Icons
import { 
  Plus, 
  Target, 
  TrendingUp, 
  Users, 
  User,
  Calendar,
  BarChart3,
  Settings,
  ChevronRight,
  Building2,
  Flag,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  LogOut,
  Eye,
  EyeOff,
  Activity,
  PieChart,
  ArrowRight,
  Shield,
  UserPlus,
  Edit,
  Trash2,
  UserCheck,
  Home as HomeIcon,
  ClipboardList,
  FileText,
  RefreshCw,
  Archive
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Try to get user info from localStorage if not already set
      if (!user) {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (e) {
            console.error('Error parsing stored user:', e);
          }
        }
      }
    }
    setLoading(false);
  }, [token, user]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Force a page refresh to ensure proper routing
      window.location.href = '/';
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  const refreshUser = async () => {
    try {
      if (token) {
        const response = await axios.get(`${API}/users/me`);
        const updatedUser = response.data;
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return updatedUser;
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
      // If refresh fails, user might need to re-login
      if (error.response?.status === 401) {
        logout();
      }
    }
    return null;
  };

  const isSuperUser = () => user?.role === 'Super User';
  const isModifyUser = () => user?.role === 'ModifyUser' || user?.role === 'Super User';
  const isReadOnlyUser = () => user?.role === 'ReadOnly User';

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      logout, 
      refreshUser,
      loading, 
      isSuperUser, 
      isModifyUser, 
      isReadOnlyUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Page Component
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Building2 className="h-12 w-12 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">PST Manager</h1>
          <p className="text-gray-600 text-sm">Plan Stratégique Transversal</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="email">Adresse e-mail</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre.email@commune.be"
              required
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <div className="relative mt-2">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="pr-10"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Connexion...
              </div>
            ) : (
              'Se connecter'
            )}
          </Button>
        </form>


      </div>
    </div>
  );
};

// User Management Components
const CreateUserDialog = ({ onUserCreated }) => {
  const [open, setOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'ReadOnly User',
    department: 'none',
    pst_access: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchDepartments();
      fetchPlans();
    }
  }, [open]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };
  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/users`, formData);
      onUserCreated(response.data);
      setOpen(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'ReadOnly User',
        department: 'none',
        pst_access: []
      });
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Erreur lors de la création de l\'utilisateur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white">
          <UserPlus className="w-4 h-4 mr-2" />
          Créer un Utilisateur
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Créer un Nouvel Utilisateur</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nom complet *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Jean Dupont"
              required
            />
          </div>

          <div>
            <Label htmlFor="email">Adresse e-mail *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="jean.dupont@commune.be"
              required
            />
          </div>

          <div>
            <Label htmlFor="password">Mot de passe *</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              required
            />
          </div>

          <div>
            <Label htmlFor="role">Rôle *</Label>
            <Select 
              value={formData.role} 
              onValueChange={(value) => setFormData({ ...formData, role: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Super User">Super User</SelectItem>
                <SelectItem value="ModifyUser">ModifyUser</SelectItem>
                <SelectItem value="ReadOnly User">ReadOnly User</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="department">Département</Label>
            <Select 
              value={formData.department || 'none'} 
              onValueChange={(value) => setFormData({ ...formData, department: value === 'none' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun département</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.name}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Accès PST</Label>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto">
              {plans.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun plan disponible</p>
              ) : (
                <div className="space-y-2">
                  {plans.map((plan) => (
                    <div key={plan.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`plan-${plan.id}`}
                        checked={formData.pst_access.includes(plan.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, pst_access: [...formData.pst_access, plan.id] });
                          } else {
                            setFormData({ ...formData, pst_access: formData.pst_access.filter(id => id !== plan.id) });
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor={`plan-${plan.id}`} className="text-sm flex-1">
                        {plan.name} ({plan.start_year}-{plan.end_year})
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {formData.pst_access.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                {formData.pst_access.length} plan(s) sélectionné(s)
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Création...' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditUserDialog = ({ user, onUserUpdated, onCurrentUserUpdated }) => {
  const [open, setOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'ReadOnly User',
    department: user?.department || 'none',
    password: '',
    pst_access: user?.pst_access || []
  });
  const [loading, setLoading] = useState(false);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        department: user.department || 'none',
        password: '',
        pst_access: user.pst_access || []
      });
    }
  }, [user]);

  useEffect(() => {
    if (open) {
      fetchDepartments();
      fetchPlans();
    }
  }, [open]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };
  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }

      const response = await axios.put(`${API}/admin/users/${user.id}`, updateData);
      onUserUpdated(response.data);
      
      // If the updated user is the currently logged-in user, refresh their data
      if (currentUser && user.id === currentUser.id && onCurrentUserUpdated) {
        await onCurrentUserUpdated();
      }
      
      setOpen(false);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Erreur lors de la mise à jour de l\'utilisateur');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Edit className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Modifier l'Utilisateur</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="edit-name">Nom complet *</Label>
            <Input
              id="edit-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="edit-email">Adresse e-mail *</Label>
            <Input
              id="edit-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="edit-role">Rôle *</Label>
            <Select 
              value={formData.role} 
              onValueChange={(value) => setFormData({ ...formData, role: value })}
              disabled={user.id === currentUser.id && currentUser.role === 'Super User'}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Super User">Super User</SelectItem>
                <SelectItem value="ModifyUser">ModifyUser</SelectItem>
                <SelectItem value="ReadOnly User">ReadOnly User</SelectItem>
              </SelectContent>
            </Select>
            {user.id === currentUser.id && currentUser.role === 'Super User' && (
              <p className="text-xs text-gray-500 mt-1">
                Vous ne pouvez pas modifier votre propre rôle Super User
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="edit-department">Département</Label>
            <Select 
              value={formData.department || 'none'} 
              onValueChange={(value) => setFormData({ ...formData, department: value === 'none' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun département</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.name}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="edit-password">Nouveau mot de passe (optionnel)</Label>
            <Input
              id="edit-password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Laisser vide pour ne pas changer"
            />
          </div>

          <div>
            <Label>Accès PST</Label>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto">
              {plans.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun plan disponible</p>
              ) : (
                <div className="space-y-2">
                  {plans.map((plan) => {
                    const hasAccess = formData.pst_access.includes(plan.id);
                    const originalAccess = user?.pst_access?.includes(plan.id) || false;
                    
                    return (
                      <div key={plan.id} className={`flex items-center space-x-2 p-2 rounded ${originalAccess ? 'bg-green-50 border border-green-200' : ''}`}>
                        <input
                          type="checkbox"
                          id={`edit-plan-${plan.id}`}
                          checked={hasAccess}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, pst_access: [...formData.pst_access, plan.id] });
                            } else {
                              setFormData({ ...formData, pst_access: formData.pst_access.filter(id => id !== plan.id) });
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <label htmlFor={`edit-plan-${plan.id}`} className={`text-sm flex-1 ${originalAccess ? 'text-green-800 font-medium' : ''}`}>
                          {plan.name} ({plan.start_year}-{plan.end_year})
                          {originalAccess && (
                            <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                              Accès existant
                            </span>
                          )}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {formData.pst_access.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                {formData.pst_access.length} plan(s) sélectionné(s)
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Logs Component
const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    dateFilter: 'all' // 'all', 'today', 'week', 'month'
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      // For now, create mock logs since backend endpoint doesn't exist yet
      // In a real implementation, this would call /api/admin/logs
      const mockLogs = [
        {
          id: '1',
          user_name: 'Admin User',
          action: 'Créé un utilisateur',
          details: 'Utilisateur: John Doe (john.doe@demo.be)',
          timestamp: new Date().toISOString(),
          type: 'USER_CREATE'
        },
        {
          id: '2', 
          user_name: 'Admin User',
          action: 'Modifié une action',
          details: 'Action: Améliorer les services numériques',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          type: 'ACTION_UPDATE'
        },
        {
          id: '3',
          user_name: 'Manager User',
          action: 'Créé un plan stratégique',
          details: 'Plan: PST 2025-2030',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          type: 'PLAN_CREATE'
        }
      ];
      setLogs(mockLogs);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'USER_CREATE':
      case 'USER_UPDATE':
        return <Users className="w-4 h-4 text-blue-500" />;
      case 'ACTION_CREATE':
      case 'ACTION_UPDATE':
        return <Target className="w-4 h-4 text-green-500" />;
      case 'PLAN_CREATE':
      case 'PLAN_UPDATE':
        return <Flag className="w-4 h-4 text-purple-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Journal d'Activité Système
          </CardTitle>
          <p className="text-sm text-gray-600">
            Suivi de toutes les actions effectuées par les utilisateurs
          </p>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <Input
                placeholder="Rechercher dans les logs..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <Select 
              value={filters.dateFilter} 
              onValueChange={(value) => setFilters({ ...filters, dateFilter: value })}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les dates</SelectItem>
                <SelectItem value="today">Aujourd'hui</SelectItem>
                <SelectItem value="week">Cette semaine</SelectItem>
                <SelectItem value="month">Ce mois</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Logs List */}
          <div className="space-y-3">
            {logs.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">Aucun log disponible</p>
              </div>
            ) : (
              logs.map((log) => (
                <div key={log.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {getActionIcon(log.type)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{log.user_name}</span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-700">{log.action}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{log.details}</p>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(log.timestamp)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Administration = () => {
  const [users, setUsers] = useState([]);
  const [organization, setOrganization] = useState('');
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [organizationSaving, setOrganizationSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Logo management state
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [currentLogo, setCurrentLogo] = useState(null);
  
  // Responsable politique state
  const [responsablePolitique, setResponsablePolitique] = useState({
    titre: '',
    prenom: '',
    nom_famille: ''
  });
  const [responsableSaving, setResponsableSaving] = useState(false);
  
  const { user: currentUser, refreshUser } = useAuth();

  useEffect(() => {
    fetchUsers();
    fetchOrganization();
    fetchLogo();
    fetchResponsablePolitique();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganization = async () => {
    try {
      setOrganizationLoading(true);
      const response = await axios.get(`${API}/admin/organization`);
      setOrganization(response.data.name);
    } catch (error) {
      console.error('Error fetching organization:', error);
    } finally {
      setOrganizationLoading(false);
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/organization/logo`);
      if (response.data) {
        setCurrentLogo(`${API}/organization/logo/file`);
      }
    } catch (error) {
      // No logo found, that's okay
      setCurrentLogo(null);
    }
  };

  const fetchResponsablePolitique = async () => {
    try {
      const response = await axios.get(`${API}/organization/responsable-politique`);
      if (response.data) {
        setResponsablePolitique(response.data);
      }
    } catch (error) {
      // No responsable politique found, that's okay
      setResponsablePolitique({
        titre: '',
        prenom: '',
        nom_famille: ''
      });
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Type de fichier non autorisé. Seuls PNG, JPEG, JPG et GIF sont acceptés.');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Fichier trop volumineux. Maximum 2MB autorisé.');
      return;
    }

    try {
      setLogoUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/organization/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      await fetchLogo();
      alert('Logo téléchargé avec succès. Le logo sera visible dans l\'en-tête après actualisation de la page.');
      // Force a reload to refresh the logo in header
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Erreur lors du téléchargement du logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer le logo ?')) return;

    try {
      await axios.delete(`${API}/organization/logo`);
      setCurrentLogo(null);
      alert('Logo supprimé avec succès. L\'en-tête sera mis à jour après actualisation de la page.');
      // Force a reload to refresh the logo in header
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error('Error deleting logo:', error);
      alert('Erreur lors de la suppression du logo');
    }
  };

  const handleSaveResponsablePolitique = async () => {
    if (!responsablePolitique.titre || !responsablePolitique.prenom || !responsablePolitique.nom_famille) {
      alert('Tous les champs sont obligatoires');
      return;
    }

    try {
      setResponsableSaving(true);
      await axios.put(`${API}/organization/responsable-politique`, responsablePolitique);
      alert('Responsable politique sauvegardé avec succès');
    } catch (error) {
      console.error('Error saving responsable politique:', error);
      alert('Erreur lors de la sauvegarde du responsable politique');
    } finally {
      setResponsableSaving(false);
    }
  };

  const handleDeleteResponsablePolitique = async () => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer les informations du responsable politique ?')) return;

    try {
      await axios.delete(`${API}/organization/responsable-politique`);
      setResponsablePolitique({
        titre: '',
        prenom: '',
        nom_famille: ''
      });
      alert('Responsable politique supprimé avec succès');
    } catch (error) {
      console.error('Error deleting responsable politique:', error);
      alert('Erreur lors de la suppression du responsable politique');
    }
  };

  const handleSaveOrganization = async () => {
    if (!organization.trim()) {
      alert('Le nom de l\'organisation ne peut pas être vide');
      return;
    }

    try {
      setOrganizationSaving(true);
      await axios.put(`${API}/admin/organization`, { name: organization.trim() });
      alert('Organisation mise à jour avec succès');
    } catch (error) {
      console.error('Error saving organization:', error);
      alert('Erreur lors de la sauvegarde de l\'organisation');
    } finally {
      setOrganizationSaving(false);
    }
  };

  const handleUserCreated = (newUser) => {
    setUsers([...users, newUser]);
  };

  const handleUserUpdated = (updatedUser) => {
    setUsers(users.map(user => user.id === updatedUser.id ? updatedUser : user));
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      setUsers(users.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      'Super User': 'bg-red-100 text-red-800 border-red-200',
      'ModifyUser': 'bg-blue-100 text-blue-800 border-blue-200',
      'ReadOnly User': 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <Badge className={`${colors[role]} border`}>
        {role}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Organization Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-blue-600" />
            Configuration Organisation
          </CardTitle>
          <p className="text-sm text-gray-600">
            Configurez le nom de l'organisation qui sera utilisé automatiquement lors de la création de nouveaux plans stratégiques.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Label htmlFor="organization-name">Nom de l'Organisation</Label>
              <Input
                id="organization-name"
                value={organizationLoading ? 'Chargement...' : organization}
                onChange={(e) => setOrganization(e.target.value)}
                placeholder="Commune de Bruxelles"
                disabled={organizationLoading || organizationSaving}
                className="mt-1"
              />
            </div>
            <Button
              onClick={handleSaveOrganization}
              disabled={organizationLoading || organizationSaving || !organization.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {organizationSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logo Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2 text-green-600" />
            Gestion du Logo
          </CardTitle>
          <p className="text-sm text-gray-600">
            Gérez le logo de l'organisation qui sera affiché en haut de la page et dans les rapports.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Current Logo Display */}
            {currentLogo && (
              <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                <img 
                  src={currentLogo} 
                  alt="Logo actuel" 
                  className="w-16 h-16 object-contain bg-white rounded border"
                />
                <div className="flex-1">
                  <p className="font-medium">Logo actuel</p>
                  <p className="text-sm text-gray-600">Le logo est affiché en haut de la page et dans les rapports</p>
                </div>
                <Button
                  variant="outline"
                  onClick={handleDeleteLogo}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Supprimer
                </Button>
              </div>
            )}

            {/* Logo Upload */}
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <Label htmlFor="logo-upload">
                  {currentLogo ? 'Remplacer le logo' : 'Télécharger un logo'}
                </Label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                  onChange={handleLogoUpload}
                  disabled={logoUploading}
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formats acceptés: PNG, JPEG, JPG, GIF. Taille maximum: 2MB
                </p>
              </div>
            </div>

            {logoUploading && (
              <div className="flex items-center space-x-2 text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Téléchargement en cours...</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Responsable Politique */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="h-5 w-5 mr-2 text-purple-600" />
            Responsable Politique
          </CardTitle>
          <p className="text-sm text-gray-600">
            Informations du responsable politique qui apparaîtront dans les rapports.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="titre">Titre *</Label>
                <Select 
                  value={responsablePolitique.titre} 
                  onValueChange={(value) => setResponsablePolitique({...responsablePolitique, titre: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Madame">Madame</SelectItem>
                    <SelectItem value="Monsieur">Monsieur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="prenom">Prénom *</Label>
                <Input
                  id="prenom"
                  value={responsablePolitique.prenom}
                  onChange={(e) => setResponsablePolitique({...responsablePolitique, prenom: e.target.value})}
                  placeholder="Jean"
                  disabled={responsableSaving}
                />
              </div>

              <div>
                <Label htmlFor="nom-famille">Nom de famille *</Label>
                <Input
                  id="nom-famille"
                  value={responsablePolitique.nom_famille}
                  onChange={(e) => setResponsablePolitique({...responsablePolitique, nom_famille: e.target.value})}
                  placeholder="Dupont"
                  disabled={responsableSaving}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                onClick={handleSaveResponsablePolitique}
                disabled={responsableSaving || !responsablePolitique.titre || !responsablePolitique.prenom || !responsablePolitique.nom_famille}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {responsableSaving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>

              {(responsablePolitique.titre || responsablePolitique.prenom || responsablePolitique.nom_famille) && (
                <Button
                  variant="outline"
                  onClick={handleDeleteResponsablePolitique}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Supprimer
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <Shield className="h-6 w-6 mr-2 text-blue-600" />
            Administration des Utilisateurs
          </h2>
          <p className="text-gray-600 mt-1">Gérer les utilisateurs et leurs rôles</p>
        </div>
        <CreateUserDialog onUserCreated={handleUserCreated} />
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead>Département</TableHead>
                <TableHead>Créé le</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center">
                      <UserCheck className="h-4 w-4 mr-2 text-gray-500" />
                      {user.name}
                      {user.id === currentUser.id && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          Vous
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>{user.department || '-'}</TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <EditUserDialog 
                        user={user} 
                        onUserUpdated={handleUserUpdated}
                        onCurrentUserUpdated={refreshUser}
                      />
                      {user.id !== currentUser.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Role Descriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Description des Rôles</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-2">
                {getRoleBadge('Super User')}
              </div>
              <p className="text-sm text-gray-600">
                Accès complet : peut créer, modifier et supprimer tous les contenus et gérer les utilisateurs.
              </p>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-2">
                {getRoleBadge('ModifyUser')}
              </div>
              <p className="text-sm text-gray-600">
                Peut créer et modifier les plans, visions, objectifs et actions. Ne peut pas gérer les utilisateurs.
              </p>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-2">
                {getRoleBadge('ReadOnly User')}
              </div>
              <p className="text-sm text-gray-600">
                Accès en lecture seule : peut consulter tous les contenus mais ne peut rien modifier.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// RAG Status Badge Component
const RAGBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'green':
        return { color: 'bg-emerald-500', icon: CheckCircle, text: 'On Track' };
      case 'amber':
        return { color: 'bg-amber-500', icon: AlertTriangle, text: 'At Risk' };
      case 'red':
        return { color: 'bg-red-500', icon: XCircle, text: 'Off Track' };
      default:
        return { color: 'bg-slate-400', icon: Clock, text: 'Not Started' };
    }
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <Badge className={`${config.color} text-white border-0 px-2 py-1`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.text}
    </Badge>
  );
};
const StrategicObjectivesManager = ({ panel, onBack }) => {
  const [strategicObjectives, setStrategicObjectives] = useState([]);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [operationalObjectives, setOperationalObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list', 'operational'
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [objectiveToEdit, setObjectiveToEdit] = useState(null);
  const [objectiveToDelete, setObjectiveToDelete] = useState(null);
  const { isModifyUser, isSuperUser } = useAuth();

  useEffect(() => {
    fetchStrategicObjectives();
  }, []);

  const fetchStrategicObjectives = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/panels/${panel.id}/strategic-objectives`);
      setStrategicObjectives(response.data);
    } catch (error) {
      console.error('Error fetching strategic objectives:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOperationalObjectives = async (strategicObjId) => {
    try {
      const response = await axios.get(`${API}/strategic-objectives/${strategicObjId}/operational-objectives`);
      setOperationalObjectives(response.data);
    } catch (error) {
      console.error('Error fetching operational objectives:', error);
    }
  };

  const handleObjectiveCreated = (newObjective) => {
    setStrategicObjectives([...strategicObjectives, newObjective]);
  };

  const handleObjectiveUpdated = (updatedObjective) => {
    setStrategicObjectives(strategicObjectives.map(obj => 
      obj.id === updatedObjective.id ? updatedObjective : obj
    ));
  };

  const handleEditObjective = (objective) => {
    setObjectiveToEdit(objective);
    setEditDialogOpen(true);
  };

  const handleDeleteObjective = (objective) => {
    setObjectiveToDelete(objective);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!objectiveToDelete) return;
    
    try {
      await axios.delete(`${API}/strategic-objectives/${objectiveToDelete.id}`);
      setStrategicObjectives(strategicObjectives.filter(obj => obj.id !== objectiveToDelete.id));
      setDeleteDialogOpen(false);
      setObjectiveToDelete(null);
    } catch (error) {
      console.error('Error deleting strategic objective:', error);
      alert('Erreur lors de la suppression de l\'objectif stratégique');
    }
  };

  const handleOperationalObjectiveCreated = (newOperationalObj) => {
    setOperationalObjectives([...operationalObjectives, newOperationalObj]);
  };

  const handleViewOperational = async (objective) => {
    setSelectedObjective(objective);
    await fetchOperationalObjectives(objective.id);
    setView('operational');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (view === 'operational' && selectedObjective) {
    return (
      <OperationalObjectivesManager 
        strategicObjective={selectedObjective}
        operationalObjectives={operationalObjectives}
        onOperationalObjectiveCreated={handleOperationalObjectiveCreated}
        onBack={() => setView('list')}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
            Retour aux Volets
          </Button>
          <div className="h-6 w-px bg-gray-300"></div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Objectifs Stratégiques</h2>
            <p className="text-gray-600">Volet {panel.title} - {panel.type.charAt(0).toUpperCase() + panel.type.slice(1)}</p>
          </div>
        </div>
        {isModifyUser() && (
          <CreateStrategicObjectiveDialog 
            panelId={panel.id} 
            onObjectiveCreated={handleObjectiveCreated}
          />
        )}
      </div>

      {/* Strategic Objectives List */}
      {strategicObjectives.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun Objectif Stratégique</h3>
            <p className="text-gray-600 mb-4">Commencez par créer votre premier objectif stratégique</p>
            {isModifyUser() && (
              <CreateStrategicObjectiveDialog 
                panelId={panel.id} 
                onObjectiveCreated={handleObjectiveCreated}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {strategicObjectives.map((objective, index) => (
            <Card key={objective.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="font-bold text-blue-600">{objective.code || `O.S.${index + 1}`}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg break-words">{objective.title}</CardTitle>
                      {objective.description && (
                        <p className="text-gray-600 text-sm mt-1 break-words">{objective.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">
                      {objective.operational_objectives?.length || 0} O.O.
                    </Badge>
                    <RAGBadge status={objective.rag_status} />
                    {isSuperUser() && (
                      <div className="flex items-center space-x-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditObjective(objective)}
                          className="h-8 w-8 p-0 hover:bg-blue-100"
                        >
                          <Edit className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteObjective(objective)}
                          className="h-8 w-8 p-0 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Progression</span>
                    <span className="font-medium">{Math.round(objective.progress_percentage)}%</span>
                  </div>
                  <Progress value={objective.progress_percentage} className="h-2" />
                  
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      {objective.service && (
                        <div className="flex items-center">
                          <Building2 className="h-4 w-4 mr-1" />
                          {objective.service}
                        </div>
                      )}
                    </div>
                    <Button 
                      onClick={() => handleViewOperational(objective)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      size="sm"
                    >
                      Gérer les O.O.
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Strategic Objective Dialog */}
      {objectiveToEdit && (
        <EditStrategicObjectiveDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setObjectiveToEdit(null);
          }}
          objective={objectiveToEdit}
          onObjectiveUpdated={handleObjectiveUpdated}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Êtes-vous sûr de vouloir supprimer l'objectif stratégique "{objectiveToDelete?.title}" ?
            </p>
            <p className="text-red-600 text-sm mt-2">
              ⚠️ Cette action supprimera également tous les objectifs opérationnels, actions et étapes associées.
              Une copie de sauvegarde sera créée pour permettre la récupération.
            </p>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Actions Management Component
const ActionsManager = ({ operationalObjective, onBack }) => {
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { isModifyUser } = useAuth();

  useEffect(() => {
    fetchActions();
    fetchUsers();
  }, []);

  const fetchActions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/operational-objectives/${operationalObjective.id}/actions`);
      setActions(response.data);
    } catch (error) {
      console.error('Error fetching actions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleActionCreated = (newAction) => {
    setActions([...actions, newAction]);
  };

  const getUsersByIds = (userIds) => {
    return users.filter(user => userIds.includes(user.id));
  };

  const handleViewAction = (action) => {
    setSelectedAction(action);
    setViewMode('detail');
  };

  const handleEditAction = (action) => {
    setSelectedAction(action);
    setEditDialogOpen(true);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedAction(null);
  };

  const handleActionUpdated = (updatedAction) => {
    setActions(actions.map(action => action.id === updatedAction.id ? updatedAction : action));
    setEditDialogOpen(false);
    setSelectedAction(null);
  };

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'Non Démarré': { color: 'bg-slate-100 text-slate-800' },
      'A l\'étude': { color: 'bg-purple-100 text-purple-800' },
      'Planifié': { color: 'bg-yellow-100 text-yellow-800' },
      'En Cours': { color: 'bg-blue-100 text-blue-800' },
      'Bloqué': { color: 'bg-red-100 text-red-800' },
      'Abandonné': { color: 'bg-gray-100 text-gray-800' },
      'Terminé': { color: 'bg-green-100 text-green-800' }
    };

    const config = statusConfig[status] || statusConfig['A l\'étude'];

    return (
      <Badge className={config.color}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show detail view if selected
  if (viewMode === 'detail' && selectedAction) {
    return <ViewActionPage action={selectedAction} users={users} onBack={handleBackToList} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
            Retour aux O.O.
          </Button>
          <div className="h-6 w-px bg-gray-300"></div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Actions</h2>
            <p className="text-gray-600">{operationalObjective.code} - {operationalObjective.title}</p>
          </div>
        </div>
        {isModifyUser() && (
          <CreateActionDialog 
            operationalObjectiveId={operationalObjective.id} 
            onActionCreated={handleActionCreated}
          />
        )}
      </div>

      {/* Operational Objective Summary */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                <span className="font-bold text-white text-sm">{operationalObjective.code}</span>
              </div>
              <div>
                <h4 className="font-semibold text-green-900">{operationalObjective.title}</h4>
                {operationalObjective.description && (
                  <p className="text-green-700 text-sm">{operationalObjective.description}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-green-700">Progression: {Math.round(operationalObjective.progress_percentage)}%</div>
              <Progress value={operationalObjective.progress_percentage} className="h-2 mt-1 w-24" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions List */}
      {actions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Activity className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune Action</h3>
            <p className="text-gray-600 mb-4">Créez les actions pour cet objectif opérationnel</p>
            {isModifyUser() && (
              <CreateActionDialog 
                operationalObjectiveId={operationalObjective.id} 
                onActionCreated={handleActionCreated}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {actions.map((action, index) => (
            <Card key={action.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          <span className="font-bold text-orange-600 text-sm">A{index + 1}</span>
                        </div>
                        <h4 className="font-semibold text-lg">{action.title}</h4>
                      </div>
                      <div className="flex items-center space-x-2">
                        <StatusBadge status={action.status} />
                        <RAGBadge status={action.rag_status} />
                      </div>
                    </div>
                    {action.description && (
                      <p className="text-gray-600 text-sm mb-3 ml-11">{action.description}</p>
                    )}
                    {/* Step Count */}
                    <div className="flex items-center space-x-2 ml-11 mb-2">
                      <ClipboardList className="h-4 w-4 text-gray-400" />
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Étapes:</span>{' '}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          (action.step_count || 0) > 0 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {action.step_count || 0} étape{(action.step_count || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {/* Participants */}
                    {action.participants && action.participants.length > 0 && (
                      <div className="flex items-center space-x-2 ml-11 mb-2">
                        <Users className="h-4 w-4 text-gray-400" />
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Participants:</span>{' '}
                          {getUsersByIds(action.participants).length > 0 ? (
                            getUsersByIds(action.participants).length > 3 ? (
                              <>
                                {getUsersByIds(action.participants).slice(0, 3).map(user => user.name).join(', ')}
                                <span className="text-gray-500"> +{getUsersByIds(action.participants).length - 3} autres</span>
                              </>
                            ) : (
                              getUsersByIds(action.participants).map(user => user.name).join(', ')
                            )
                          ) : (
                            <span className="text-gray-400">Utilisateurs non trouvés</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Progression</span>
                      <span className="font-medium">{Math.round(action.progress_percentage)}%</span>
                    </div>
                    <Progress value={action.progress_percentage} className="h-2" />
                  </div>

                  {/* Dates */}
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">Période</div>
                    <div className="text-sm">
                      {action.start_date && action.end_date ? (
                        <span>
                          {new Date(action.start_date).toLocaleDateString('fr-FR')} - {new Date(action.end_date).toLocaleDateString('fr-FR')}
                        </span>
                      ) : (
                        <span className="text-gray-400">Non définie</span>
                      )}
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">Budget</div>
                    <div className="text-sm">
                      {action.budget_planned ? (
                        <span className="font-medium">
                          {action.budget_planned.toLocaleString('fr-FR')} €
                          {action.budget_actual && (
                            <span className="text-gray-500 ml-1">
                              (Réel: {action.budget_actual.toLocaleString('fr-FR')} €)
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400">Non défini</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    {action.department && (
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 mr-1" />
                        {action.department}
                      </div>
                    )}
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      Créé le {new Date(action.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleViewAction(action)} 
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Voir cette action"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {isModifyUser() && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleEditAction(action)} 
                        className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                        title="Modifier cette action"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Action Dialog */}
      {selectedAction && (
        <EditActionDialog 
          action={selectedAction}
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onActionUpdated={handleActionUpdated}
          users={users}
        />
      )}
    </div>
  );
};

// Create Action Dialog
const CreateActionDialog = ({ operationalObjectiveId, onActionCreated }) => {
  const [open, setOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'Non Démarré',
    department: '',
    participants: [],
    start_date: '',
    end_date: '',
    budget_planned: ''
  });
  const [loading, setLoading] = useState(false);

  const statusOptions = [
    'Non Démarré',
    'A l\'étude', 
    'Planifié',
    'En Cours',
    'Bloqué',
    'Abandonné',
    'Terminé'
  ];

  useEffect(() => {
    if (open) {
      fetchDepartments();
      fetchUsers();
    }
  }, [open]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleParticipantToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.includes(userId)
        ? prev.participants.filter(id => id !== userId)
        : [...prev.participants, userId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title.trim()) {
      alert('Le titre de l\'action est obligatoire');
      return;
    }

    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Convert dates to proper format
      if (submitData.start_date) {
        submitData.start_date = new Date(submitData.start_date).toISOString();
      }
      if (submitData.end_date) {
        submitData.end_date = new Date(submitData.end_date).toISOString();
      }
      
      // Convert budget to number, default to 0 if empty
      if (submitData.budget_planned) {
        submitData.budget_planned = parseFloat(submitData.budget_planned);
      } else {
        submitData.budget_planned = 0;
      }

      const response = await axios.post(`${API}/operational-objectives/${operationalObjectiveId}/actions`, submitData);
      onActionCreated(response.data);
      setOpen(false);
      setFormData({
        title: '',
        description: '',
        status: 'Non Démarré',
        department: '',
        participants: [],
        start_date: '',
        end_date: '',
        budget_planned: ''
      });
    } catch (error) {
      console.error('Error creating action:', error);
      alert('Erreur lors de la création de l\'action');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-orange-600 hover:bg-orange-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Créer une Action
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Créer une Action</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="action-title">Titre de l'Action *</Label>
            <Input
              id="action-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Formation du personnel d'accueil"
              required
            />
          </div>

          <div>
            <Label htmlFor="action-description">Description</Label>
            <Textarea
              id="action-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description détaillée de l'action à réaliser..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="action-status">Status</Label>
            <Select 
              value={formData.status} 
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="action-department">Département</Label>
            <Select 
              value={formData.department} 
              onValueChange={(value) => setFormData({ ...formData, department: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un département" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.name}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Participants</Label>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto">
              {users.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun utilisateur disponible</p>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`user-${user.id}`}
                        checked={formData.participants.includes(user.id)}
                        onChange={() => handleParticipantToggle(user.id)}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor={`user-${user.id}`} className="text-sm flex-1 flex items-center justify-between">
                        <span>{user.name}</span>
                        <span className="text-xs text-gray-500">{user.email}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {formData.participants.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                {formData.participants.length} participant(s) sélectionné(s)
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start-date">Date de Début</Label>
              <Input
                id="start-date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="end-date">Date de Fin</Label>
              <Input
                id="end-date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="budget">Budget Prévu (€)</Label>
            <Input
              id="budget"
              type="number"
              value={formData.budget_planned}
              onChange={(e) => setFormData({ ...formData, budget_planned: e.target.value })}
              placeholder="15000"
              min="0"
              step="0.01"
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={loading}
            >
              {loading ? 'Création...' : 'Créer Action'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
const OperationalObjectivesManager = ({ strategicObjective, operationalObjectives, onOperationalObjectiveCreated, onBack }) => {
  const { isModifyUser, isSuperUser } = useAuth();
  const [currentView, setCurrentView] = useState('operational-objectives'); // 'operational-objectives', 'actions'
  const [selectedOperationalObjective, setSelectedOperationalObjective] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [objectiveToEdit, setObjectiveToEdit] = useState(null);
  const [objectiveToDelete, setObjectiveToDelete] = useState(null);
  const [objectives, setObjectives] = useState(operationalObjectives);

  useEffect(() => {
    setObjectives(operationalObjectives);
  }, [operationalObjectives]);

  const handleManageActions = (operationalObj) => {
    setSelectedOperationalObjective(operationalObj);
    setCurrentView('actions');
  };

  const handleBackToOperationalObjectives = () => {
    setCurrentView('operational-objectives');
    setSelectedOperationalObjective(null);
  };

  const handleEditObjective = (objective) => {
    setObjectiveToEdit(objective);
    setEditDialogOpen(true);
  };

  const handleDeleteObjective = (objective) => {
    setObjectiveToDelete(objective);
    setDeleteDialogOpen(true);
  };

  const handleObjectiveUpdated = (updatedObjective) => {
    setObjectives(objectives.map(obj => 
      obj.id === updatedObjective.id ? updatedObjective : obj
    ));
  };

  const handleConfirmDelete = async () => {
    if (!objectiveToDelete) return;
    
    try {
      await axios.delete(`${API}/operational-objectives/${objectiveToDelete.id}`);
      setObjectives(objectives.filter(obj => obj.id !== objectiveToDelete.id));
      setDeleteDialogOpen(false);
      setObjectiveToDelete(null);
    } catch (error) {
      console.error('Error deleting operational objective:', error);
      alert('Erreur lors de la suppression de l\'objectif opérationnel');
    }
  };

  // Show Actions Manager
  if (currentView === 'actions' && selectedOperationalObjective) {
    return (
      <ActionsManager 
        operationalObjective={selectedOperationalObjective} 
        onBack={handleBackToOperationalObjectives}
      />
    );
  }

  // Show Operational Objectives List (default view)
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
            Retour aux O.S.
          </Button>
          <div className="h-6 w-px bg-gray-300"></div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Objectifs Opérationnels</h2>
            <p className="text-gray-600">{strategicObjective.code} - {strategicObjective.title}</p>
          </div>
        </div>
        {isModifyUser() && (
          <CreateOperationalObjectiveDialog 
            strategicObjectiveId={strategicObjective.id} 
            onObjectiveCreated={onOperationalObjectiveCreated}
          />
        )}
      </div>

      {/* Strategic Objective Summary */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="font-bold text-white text-sm">{strategicObjective.code}</span>
              </div>
              <div>
                <h4 className="font-semibold text-blue-900">{strategicObjective.title}</h4>
                {strategicObjective.description && (
                  <p className="text-blue-700 text-sm">{strategicObjective.description}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-blue-700">Progression: {Math.round(strategicObjective.progress_percentage)}%</div>
              <Progress value={strategicObjective.progress_percentage} className="h-2 mt-1 w-24" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Operational Objectives List */}
      {objectives.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun Objectif Opérationnel</h3>
            <p className="text-gray-600 mb-4">Créez les objectifs opérationnels pour cet objectif stratégique</p>
            {isModifyUser() && (
              <CreateOperationalObjectiveDialog 
                strategicObjectiveId={strategicObjective.id} 
                onObjectiveCreated={onOperationalObjectiveCreated}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {objectives.map((operationalObj, index) => (
            <Card key={operationalObj.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="font-bold text-green-600 text-sm">
                        {operationalObj.code || `O.O.${index + 1}`}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base break-words">{operationalObj.title}</CardTitle>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RAGBadge status={operationalObj.rag_status} />
                    {isSuperUser() && (
                      <div className="flex items-center space-x-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditObjective(operationalObj)}
                          className="h-8 w-8 p-0 hover:bg-green-100"
                        >
                          <Edit className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteObjective(operationalObj)}
                          className="h-8 w-8 p-0 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {operationalObj.description && (
                  <p className="text-gray-600 text-sm mt-2 break-words">{operationalObj.description}</p>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Progression</span>
                    <span className="font-medium">{Math.round(operationalObj.progress_percentage)}%</span>
                  </div>
                  <Progress value={operationalObj.progress_percentage} className="h-2" />
                  
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center space-x-2 text-xs text-gray-600">
                      {operationalObj.service && (
                        <div className="flex items-center">
                          <Building2 className="h-3 w-3 mr-1" />
                          {operationalObj.service}
                        </div>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {operationalObj.actions?.length || 0} Actions
                      </Badge>
                    </div>
                    <Button 
                      onClick={() => handleManageActions(operationalObj)}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      size="sm"
                      disabled={!isModifyUser()}
                    >
                      <ArrowRight className="h-3 w-3 mr-1" />
                      Gérer Actions
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Operational Objective Dialog */}
      {objectiveToEdit && (
        <EditOperationalObjectiveDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setObjectiveToEdit(null);
          }}
          objective={objectiveToEdit}
          onObjectiveUpdated={handleObjectiveUpdated}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Êtes-vous sûr de vouloir supprimer l'objectif opérationnel "{objectiveToDelete?.title}" ?
            </p>
            <p className="text-red-600 text-sm mt-2">
              ⚠️ Cette action supprimera également toutes les actions et étapes associées.
              Une copie de sauvegarde sera créée pour permettre la récupération.
            </p>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Create Strategic Objective Dialog
const CreateStrategicObjectiveDialog = ({ panelId, onObjectiveCreated }) => {
  const [open, setOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    service: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchingCode, setFetchingCode] = useState(false);

  useEffect(() => {
    if (open && panelId) {
      fetchNextCode();
      fetchDepartments();
    }
  }, [open, panelId]);

  const fetchNextCode = async () => {
    try {
      setFetchingCode(true);
      const response = await axios.get(`${API}/panels/${panelId}/next-strategic-objective-code`);
      setFormData(prev => ({ ...prev, code: response.data.code }));
    } catch (error) {
      console.error('Error fetching next code:', error);
      setFormData(prev => ({ ...prev, code: 'O.S.1' })); // Fallback
    } finally {
      setFetchingCode(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/panels/${panelId}/strategic-objectives`, formData);
      onObjectiveCreated(response.data);
      setOpen(false);
      setFormData({ code: '', title: '', description: '', service: '' });
    } catch (error) {
      console.error('Error creating strategic objective:', error);
      alert('Erreur lors de la création de l\'objectif stratégique');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Créer un O.S.
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Créer un Objectif Stratégique</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="code">Code O.S. *</Label>
              <Input
                id="code"
                value={fetchingCode ? 'Génération...' : formData.code}
                readOnly
                className="bg-gray-50 cursor-not-allowed"
                placeholder="Auto-généré"
              />
              <p className="text-xs text-gray-500 mt-1">Code généré automatiquement</p>
            </div>
            <div>
              <Label htmlFor="department">Département</Label>
              <Select 
                value={formData.service} 
                onValueChange={(value) => setFormData({ ...formData, service: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un département" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.name}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="title">Titre de l'Objectif *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Améliorer la qualité des services"
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description détaillée de l'objectif stratégique..."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading || fetchingCode || !formData.code}
            >
              {loading ? 'Création...' : 'Créer O.S.'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit Strategic Objective Dialog
const EditStrategicObjectiveDialog = ({ open, onClose, objective, onObjectiveUpdated }) => {
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    service: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && objective) {
      setFormData({
        title: objective.title || '',
        description: objective.description || '',
        service: objective.service || ''
      });
      fetchDepartments();
    }
  }, [open, objective]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!objective) return;
    
    setLoading(true);

    try {
      const response = await axios.put(`${API}/strategic-objectives/${objective.id}`, formData);
      onObjectiveUpdated(response.data);
      onClose();
    } catch (error) {
      console.error('Error updating strategic objective:', error);
      alert('Erreur lors de la modification de l\'objectif stratégique');
    } finally {
      setLoading(false);
    }
  };

  if (!objective) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifier l'Objectif Stratégique</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-code">Code O.S.</Label>
              <Input
                id="edit-code"
                value={objective.code}
                readOnly
                className="bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Le code ne peut pas être modifié</p>
            </div>
            <div>
              <Label htmlFor="edit-department">Département</Label>
              <Select 
                value={formData.service} 
                onValueChange={(value) => setFormData({ ...formData, service: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un département" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.name}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="edit-title">Titre de l'Objectif *</Label>
            <Input
              id="edit-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Améliorer la qualité des services"
              required
            />
          </div>

          <div>
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description détaillée de l'objectif stratégique..."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading || !formData.title}
            >
              {loading ? 'Modification...' : 'Modifier O.S.'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Create Operational Objective Dialog
const CreateOperationalObjectiveDialog = ({ strategicObjectiveId, onObjectiveCreated }) => {
  const [open, setOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    service: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchingCode, setFetchingCode] = useState(false);

  useEffect(() => {
    if (open && strategicObjectiveId) {
      fetchNextCode();
      fetchDepartments();
    }
  }, [open, strategicObjectiveId]);

  const fetchNextCode = async () => {
    try {
      setFetchingCode(true);
      const response = await axios.get(`${API}/strategic-objectives/${strategicObjectiveId}/next-operational-objective-code`);
      setFormData(prev => ({ ...prev, code: response.data.code }));
    } catch (error) {
      console.error('Error fetching next code:', error);
      setFormData(prev => ({ ...prev, code: 'O.O.1' })); // Fallback
    } finally {
      setFetchingCode(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/strategic-objectives/${strategicObjectiveId}/operational-objectives`, formData);
      onObjectiveCreated(response.data);
      setOpen(false);
      setFormData({ code: '', title: '', description: '', service: '' });
    } catch (error) {
      console.error('Error creating operational objective:', error);
      alert('Erreur lors de la création de l\'objectif opérationnel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-green-600 hover:bg-green-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Créer un O.O.
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Créer un Objectif Opérationnel</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="oo-code">Code O.O. *</Label>
              <Input
                id="oo-code"
                value={fetchingCode ? 'Génération...' : formData.code}
                readOnly
                className="bg-gray-50 cursor-not-allowed"
                placeholder="Auto-généré"
              />
              <p className="text-xs text-gray-500 mt-1">Code généré automatiquement</p>
            </div>
            <div>
              <Label htmlFor="oo-department">Département</Label>
              <Select 
                value={formData.service} 
                onValueChange={(value) => setFormData({ ...formData, service: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un département" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.name}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="oo-title">Titre de l'Objectif *</Label>
            <Input
              id="oo-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Moderniser les équipements"
              required
            />
          </div>

          <div>
            <Label htmlFor="oo-description">Description</Label>
            <Textarea
              id="oo-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description détaillée de l'objectif opérationnel..."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-green-600 hover:bg-green-700"
              disabled={loading || fetchingCode || !formData.code}
            >
              {loading ? 'Création...' : 'Créer O.O.'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit Operational Objective Dialog
const EditOperationalObjectiveDialog = ({ open, onClose, objective, onObjectiveUpdated }) => {
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    code: objective?.code || '',
    title: objective?.title || '',
    description: objective?.description || '',
    service: objective?.service || 'none'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (objective) {
      setFormData({
        code: objective.code || '',
        title: objective.title || '',
        description: objective.description || '',
        service: objective.service || 'none'
      });
    }
  }, [objective]);

  useEffect(() => {
    if (open) {
      fetchDepartments();
    }
  }, [open]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert "none" back to empty string for backend
      const submitData = {
        ...formData,
        service: formData.service === 'none' ? '' : formData.service
      };
      
      const response = await axios.put(`${API}/operational-objectives/${objective.id}`, submitData);
      onObjectiveUpdated(response.data);
      onClose();
    } catch (error) {
      console.error('Error updating operational objective:', error);
      alert('Erreur lors de la mise à jour de l\'objectif opérationnel');
    } finally {
      setLoading(false);
    }
  };

  if (!objective) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifier l'Objectif Opérationnel</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="edit-code">Code O.O. *</Label>
            <Input
              id="edit-code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder="O.O.1"
              required
            />
          </div>

          <div>
            <Label htmlFor="edit-title">Titre *</Label>
            <Input
              id="edit-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Titre de l'objectif opérationnel"
              required
            />
          </div>

          <div>
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description détaillée de l'objectif"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="edit-department">Département</Label>
            <Select 
              value={formData.service} 
              onValueChange={(value) => setFormData({ ...formData, service: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun département</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.name}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-green-600 hover:bg-green-700"
              disabled={loading}
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CustomPieChart = ({ data, title, onItemClick }) => {
  const total = Object.values(data).reduce((sum, value) => sum + value, 0);
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316'];
  
  let cumulativePercentage = 0;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <PieChart className="h-5 w-5 mr-2 text-blue-600" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-center">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="transparent"
                stroke="#e5e7eb"
                strokeWidth="2"
              />
              {Object.entries(data).map(([key, value], index) => {
                const percentage = (value / total) * 100;
                const strokeDasharray = `${percentage} ${100 - percentage}`;
                const strokeDashoffset = -cumulativePercentage;
                cumulativePercentage += percentage;
                
                return (
                  <circle
                    key={key}
                    cx="18"
                    cy="18"
                    r="16"
                    fill="transparent"
                    stroke={colors[index % colors.length]}
                    strokeWidth="2"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    className="transition-all duration-500"
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-gray-900">{total}</span>
            </div>
          </div>
        </div>
        
        <div className="mt-4 space-y-2">
          {Object.entries(data).map(([key, value], index) => (
            <div 
              key={key} 
              className={`flex items-center justify-between ${
                onItemClick ? 'cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors' : ''
              }`}
              onClick={() => onItemClick && onItemClick(key, value)}
            >
              <div className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
                <span className="text-sm text-gray-600">{key}</span>
              </div>
              <span className="text-sm font-medium">{value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Dashboard Component
const Dashboard = ({ plan, onNavigate, user }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (plan) {
      fetchDashboardData();
    }
  }, [plan]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/${plan.id}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = (type, status) => {
    if (onNavigate) {
      if (type === 'actions') {
        onNavigate('mes-actions', { statusFilter: status });
      } else if (type === 'steps') {
        onNavigate('mes-activites', { statusFilter: status });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return <div>Erreur lors du chargement des données</div>;
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Progression Globale PST</p>
                <p className="text-3xl font-bold text-blue-600">{Math.round(dashboardData.overall_progress)}%</p>
              </div>
              <Activity className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onNavigate && onNavigate('actions', { planFilter: plan?.id })}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Nombre d'Actions</p>
                <p className="text-3xl font-bold text-green-600">{dashboardData.total_actions}</p>
              </div>
              <Target className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Progression Moyenne</p>
                <p className="text-3xl font-bold text-amber-600">{Math.round(dashboardData.average_action_progress)}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onNavigate && onNavigate('pst')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Visions</p>
                <p className="text-3xl font-bold text-purple-600">{dashboardData.visions_count}</p>
              </div>
              <Flag className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CustomPieChart 
          data={Object.fromEntries(
            Object.entries(dashboardData.actions_by_service).map(([key, value]) => 
              [key === 'Non défini' ? 'Actions' : key, value]
            )
          )}
          title="Actions par Département"
          onItemClick={(key, value) => {
            if (onNavigate) {
              if (key === 'Actions') {
                // When clicking "Actions", show user's department actions for the selected PST
                onNavigate('actions', { 
                  departmentFilter: user?.department,
                  planFilter: plan?.id 
                });
              } else {
                // For specific department clicks, filter by that department for the selected PST
                onNavigate('actions', { 
                  departmentFilter: key,
                  planFilter: plan?.id 
                });
              }
            }
          }}
        />
        
        <CustomPieChart 
          data={{
            "Objectifs Stratégiques": dashboardData.objectives_count.strategic,
            "Objectifs Opérationnels": dashboardData.objectives_count.operational
          }}
          title="Objectifs"
          onItemClick={(key, value) => {
            if (onNavigate) {
              onNavigate('pst'); // Navigate to PST tab where objectives are managed
            }
          }}
        />
      </div>
    </div>
  );
};

// Edit Vision Dialog Component
const EditVisionDialog = ({ vision, onVisionUpdated }) => {
  const [open, setOpen] = useState(false);
  const { isSuperUser } = useAuth();
  const [baseTitle, setBaseTitle] = useState('');
  const [startMonth, setStartMonth] = useState('Janvier');
  const [endMonth, setEndMonth] = useState('Juin');
  const [formData, setFormData] = useState({
    title: vision?.title || '',
    description: vision?.description || ''
  });
  const [loading, setLoading] = useState(false);

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  useEffect(() => {
    if (open && vision) {
      // Extract base title and months from existing title
      const titleParts = vision.title.split(' – ');
      if (titleParts.length === 3) {
        setBaseTitle(titleParts[0]);
        setStartMonth(titleParts[1]);
        setEndMonth(titleParts[2]);
      } else {
        setBaseTitle(vision.title);
        setStartMonth('Janvier');
        setEndMonth('Juin');
      }
      
      setFormData({
        title: vision.title,
        description: vision.description || ''
      });
    }
  }, [open, vision]);

  // Generate final title when base title or months change
  useEffect(() => {
    if (baseTitle && startMonth && endMonth) {
      setFormData(prev => ({
        ...prev,
        title: `${baseTitle} – ${startMonth} – ${endMonth}`
      }));
    }
  }, [baseTitle, startMonth, endMonth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!baseTitle.trim()) {
      alert('Le titre de base est obligatoire');
      return;
    }

    setLoading(true);

    try {
      const finalTitle = `${baseTitle.trim()} – ${startMonth} – ${endMonth}`;
      const submitData = {
        ...formData,
        title: finalTitle
      };

      const response = await axios.put(`${API}/visions/${vision.id}`, submitData);
      onVisionUpdated(response.data);
      setOpen(false);
    } catch (error) {
      console.error('Error updating vision:', error);
      alert('Erreur lors de la mise à jour de la vision');
    } finally {
      setLoading(false);
    }
  };

  if (!vision || !isSuperUser()) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit className="w-4 h-4 mr-2" />
          Modifier Vision
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Modifier la Vision</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="base-title">Titre de Base *</Label>
            <Input
              id="base-title"
              value={baseTitle}
              onChange={(e) => setBaseTitle(e.target.value)}
              placeholder="Ex: PST"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start-month">Mois de Début</Label>
              <Select value={startMonth} onValueChange={setStartMonth}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="end-month">Mois de Fin</Label>
              <Select value={endMonth} onValueChange={setEndMonth}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="final-title">Titre Final</Label>
            <Input
              id="final-title"
              value={formData.title}
              readOnly
              className="bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Le titre final sera généré automatiquement
            </p>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description de la vision..."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading || !baseTitle.trim()}
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Create Vision Dialog Component
const CreateVisionDialog = ({ planId, onVisionCreated }) => {
  const [open, setOpen] = useState(false);
  const [baseTitle, setBaseTitle] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });
  const { isModifyUser } = useAuth();

  // French month names
  const months = [
    { value: '1', label: 'Janvier' },
    { value: '2', label: 'Février' },
    { value: '3', label: 'Mars' },
    { value: '4', label: 'Avril' },
    { value: '5', label: 'Mai' },
    { value: '6', label: 'Juin' },
    { value: '7', label: 'Juillet' },
    { value: '8', label: 'Août' },
    { value: '9', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' }
  ];

  useEffect(() => {
    // Auto-update title when base title or months change
    if (baseTitle.trim()) {
      let newTitle = baseTitle.trim();
      
      if (startMonth && endMonth) {
        const startMonthName = months.find(m => m.value === startMonth)?.label || '';
        const endMonthName = months.find(m => m.value === endMonth)?.label || '';
        newTitle = `${baseTitle.trim()} – ${startMonthName} – ${endMonthName}`;
      } else if (startMonth) {
        const startMonthName = months.find(m => m.value === startMonth)?.label || '';
        newTitle = `${baseTitle.trim()} – ${startMonthName}`;
      }
      
      setFormData(prev => ({ ...prev, title: newTitle }));
    }
  }, [baseTitle, startMonth, endMonth]);

  const handleBaseTitleChange = (value) => {
    setBaseTitle(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/plans/${planId}/visions`, formData);
      onVisionCreated(response.data);
      setOpen(false);
      setFormData({ title: '', description: '' });
      setBaseTitle('');
      setStartMonth('');
      setEndMonth('');
    } catch (error) {
      console.error('Error creating vision:', error);
    }
  };

  if (!isModifyUser()) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une Vision
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Créer une Vision</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="base-title">Titre de Base *</Label>
            <Input
              id="base-title"
              value={baseTitle}
              onChange={(e) => handleBaseTitleChange(e.target.value)}
              placeholder="PST"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Titre final: "{baseTitle.trim() || 'Titre'}{startMonth && endMonth ? ` – ${months.find(m => m.value === startMonth)?.label} – ${months.find(m => m.value === endMonth)?.label}` : ''}"
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start-month">Mois de Début</Label>
              <Select 
                value={startMonth} 
                onValueChange={setStartMonth}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="end-month">Mois de Fin</Label>
              <Select 
                value={endMonth} 
                onValueChange={setEndMonth}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="final-title">Titre Final *</Label>
            <Input
              id="final-title"
              value={formData.title}
              readOnly
              className="bg-gray-50 cursor-not-allowed"
              placeholder="Titre généré automatiquement"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description de la vision..."
              rows={3}
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!baseTitle.trim()}
            >
              Créer
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Visions Component
const VisionsList = ({ plan }) => {
  const [visions, setVisions] = useState([]);
  const [selectedVision, setSelectedVision] = useState(null);
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('visions'); // 'visions', 'strategic-objectives'
  const [selectedPanel, setSelectedPanel] = useState(null);
  const { isModifyUser, isSuperUser } = useAuth();

  useEffect(() => {
    if (plan) {
      // Reset all related state when plan changes
      setSelectedVision(null);
      setPanels([]);
      setSelectedPanel(null);
      setCurrentView('visions');
      fetchVisions();
    }
  }, [plan]);

  const fetchVisions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/plans/${plan.id}/visions`);
      setVisions(response.data);
      // Always set the first vision as selected when fetching new visions
      if (response.data.length > 0) {
        setSelectedVision(response.data[0]);
      } else {
        setSelectedVision(null);
      }
    } catch (error) {
      console.error('Error fetching visions:', error);
      setVisions([]);
      setSelectedVision(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchPanels = async (visionId) => {
    try {
      const response = await axios.get(`${API}/visions/${visionId}/panels`);
      setPanels(response.data);
    } catch (error) {
      console.error('Error fetching panels:', error);
    }
  };

  useEffect(() => {
    if (selectedVision) {
      fetchPanels(selectedVision.id);
    }
  }, [selectedVision]);

  const handleVisionCreated = (newVision) => {
    setVisions([...visions, newVision]);
    setSelectedVision(newVision);
  };

  const handleVisionUpdated = (updatedVision) => {
    setVisions(visions.map(vision => vision.id === updatedVision.id ? updatedVision : vision));
    if (selectedVision?.id === updatedVision.id) {
      setSelectedVision(updatedVision);
    }
  };

  const createDefaultPanels = async (visionId) => {
    if (!isModifyUser()) return;
    
    try {
      // Create Externe panel
      await axios.post(`${API}/visions/${visionId}/panels`, {
        type: 'externe',
        title: 'Externe',
        description: 'Volet externe de la vision stratégique'
      });
      
      // Create Interne panel
      await axios.post(`${API}/visions/${visionId}/panels`, {
        type: 'interne',
        title: 'Interne',
        description: 'Volet interne de la vision stratégique'
      });
      
      // Refresh panels
      fetchPanels(visionId);
    } catch (error) {
      console.error('Error creating panels:', error);
    }
  };

  const handleManageStrategicObjectives = (panel) => {
    setSelectedPanel(panel);
    setCurrentView('strategic-objectives');
  };

  const handleBackToVisions = () => {
    setCurrentView('visions');
    setSelectedPanel(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show Strategic Objectives Manager
  if (currentView === 'strategic-objectives' && selectedPanel) {
    return (
      <StrategicObjectivesManager 
        panel={selectedPanel} 
        onBack={handleBackToVisions}
      />
    );
  }

  // Show Visions List (default view)
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Visions Stratégiques</h2>
        <div className="flex items-center space-x-2">
          {isSuperUser() && selectedVision && (
            <EditVisionDialog vision={selectedVision} onVisionUpdated={handleVisionUpdated} />
          )}
          <CreateVisionDialog planId={plan.id} onVisionCreated={handleVisionCreated} />
        </div>
      </div>

      {visions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Flag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune vision</h3>
            <p className="text-gray-600 mb-4">
              Commencez par créer votre première vision stratégique en utilisant le bouton "Ajouter une Vision" ci-dessus.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Vision Selector */}
          {visions.length > 1 && (
            <div className="flex space-x-2">
              {visions.map((vision) => (
                <Button
                  key={vision.id}
                  variant={selectedVision?.id === vision.id ? "default" : "outline"}
                  onClick={() => setSelectedVision(vision)}
                  className="mb-4"
                >
                  {vision.title}
                </Button>
              ))}
            </div>
          )}

          {/* Vision Content */}
          {selectedVision && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Flag className="h-5 w-5 mr-2 text-blue-600" />
                    {selectedVision.title}
                  </div>
                  <Badge variant="outline">{Math.round(selectedVision.progress_percentage)}% complété</Badge>
                </CardTitle>
                {selectedVision.description && (
                  <p className="text-gray-600">{selectedVision.description}</p>
                )}
              </CardHeader>
              <CardContent>
                {panels.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600 mb-4">Aucun volet créé pour cette vision</p>
                    {isModifyUser() && (
                      <Button 
                        onClick={() => createDefaultPanels(selectedVision.id)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Créer volets Externe/Interne
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {panels.map((panel) => (
                      <Card key={panel.id} className="border-2 hover:shadow-lg transition-shadow">
                        <CardHeader>
                          <CardTitle className="flex items-center justify-between">
                            <span className={`text-lg ${panel.type === 'externe' ? 'text-green-600' : 'text-blue-600'}`}>
                              {panel.title}
                            </span>
                            <Badge variant="outline">{Math.round(panel.progress_percentage)}%</Badge>
                          </CardTitle>
                          {panel.description && (
                            <p className="text-sm text-gray-600">{panel.description}</p>
                          )}
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Objectifs Stratégiques</span>
                              <span className="font-medium">{panel.strategic_objectives.length}</span>
                            </div>
                            <Progress value={panel.progress_percentage} className="h-2" />
                            <div className="text-center pt-4">
                              <Button 
                                onClick={() => handleManageStrategicObjectives(panel)}
                                className={`text-xs ${
                                  panel.type === 'externe' 
                                    ? 'bg-green-600 hover:bg-green-700' 
                                    : 'bg-blue-600 hover:bg-blue-700'
                                } text-white`}
                                size="sm"
                                disabled={!isModifyUser()}
                              >
                                <ArrowRight className="h-3 w-3 mr-1" />
                                Gérer les O.S.
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

// Accueil Component
const Accueil = ({ onNavigate }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [departmentActions, setDepartmentActions] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    fetchDashboardData();
    fetchDepartmentActions();
  }, []);

  // Add effect to refetch department actions when user.department changes
  useEffect(() => {
    fetchDepartmentActions();
  }, [user?.department]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users/me/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentActions = async () => {
    try {
      if (user?.department) {
        // Fetch all actions from all PSTs for the user's department
        const response = await axios.get(`${API}/actions/all`);
        const allActions = response.data;
        
        // Filter actions by user's department  
        const deptActions = allActions.filter(action => action.department === user.department);
        
        // Group by status
        const actionsByStatus = {};
        deptActions.forEach(action => {
          const status = action.status || 'Non Démarré';
          actionsByStatus[status] = (actionsByStatus[status] || 0) + 1;
        });
        
        setDepartmentActions({
          total: deptActions.length,
          by_status: actionsByStatus
        });
      } else {
        // No department assigned
        setDepartmentActions({
          total: 0,
          by_status: {}
        });
      }
    } catch (error) {
      console.error('Error fetching department actions:', error);
    }
  };

  const handleStatusClick = (type, status) => {
    if (onNavigate) {
      if (type === 'actions') {
        onNavigate('mes-actions', { statusFilter: status });
      } else if (type === 'steps') {
        onNavigate('mes-activites', { statusFilter: status });
      }
    }
  };

  const handleDepartmentStatusClick = (status) => {
    if (onNavigate) {
      // Navigate to actions tab with department filter and status filter
      onNavigate('actions', { 
        departmentFilter: user?.department,
        statusFilter: status
      });
    }
  };

  const refreshAllData = async () => {
    setLoading(true);
    try {
      // Refresh user data first
      await refreshUser();
      // Then refresh dashboard and department data
      await Promise.all([
        fetchDashboardData(),
        fetchDepartmentActions()
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const actionStatusColors = {
    'Non Démarré': 'bg-slate-100 border-slate-300 text-slate-800',
    'A l\'étude': 'bg-purple-100 border-purple-300 text-purple-800',
    'Planifié': 'bg-yellow-100 border-yellow-300 text-yellow-800',
    'En Cours': 'bg-blue-100 border-blue-300 text-blue-800',
    'Bloqué': 'bg-red-100 border-red-300 text-red-800',
    'Abandonné': 'bg-gray-100 border-gray-300 text-gray-800',
    'Terminé': 'bg-green-100 border-green-300 text-green-800'
  };

  const stepStatusColors = {
    'À faire': 'bg-gray-100 border-gray-300 text-gray-800',
    'En cours': 'bg-blue-100 border-blue-300 text-blue-800',
    'Terminées': 'bg-green-100 border-green-300 text-green-800',
    'En retard': 'bg-red-100 border-red-300 text-red-800'
  };

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-xl font-bold text-blue-600">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bienvenue, {user?.name}</h1>
              <p className="text-gray-600">Voici un aperçu de vos activités</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={refreshAllData}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Actualiser</span>
          </Button>
        </div>
      </div>

      {/* Actions Section */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <Target className="h-6 w-6 mr-2 text-blue-600" />
          Actions assigned to my profile
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {dashboardData?.actions?.by_status && Object.keys(dashboardData.actions.by_status).length > 0 ? (
            Object.entries(dashboardData.actions.by_status).map(([status, count]) => (
              <Card 
                key={status} 
                className={`border-2 cursor-pointer hover:shadow-lg transition-shadow ${actionStatusColors[status] || 'bg-gray-100 border-gray-300 text-gray-800'}`}
                onClick={() => handleStatusClick('actions', status)}
              >
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold mb-1">{count}</div>
                  <div className="text-sm font-medium">{status}</div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-8">
              <Target className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Aucune action assignée</p>
            </div>
          )}
        </div>
      </div>

      {/* Steps Section */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <CheckCircle className="h-6 w-6 mr-2 text-green-600" />
          Étapes assigned to my profile
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {dashboardData?.steps?.by_status && Object.keys(dashboardData.steps.by_status).length > 0 ? (
            Object.entries(dashboardData.steps.by_status).map(([status, count]) => (
              <Card 
                key={status} 
                className={`border-2 cursor-pointer hover:shadow-lg transition-shadow ${stepStatusColors[status] || 'bg-gray-100 border-gray-300 text-gray-800'}`}
                onClick={() => handleStatusClick('steps', status)}
              >
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold mb-1">{count}</div>
                  <div className="text-sm font-medium">{status}</div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-8">
              <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Aucune étape assignée</p>
            </div>
          )}
        </div>
      </div>

      {/* Department Actions Section */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <Building2 className="h-6 w-6 mr-2 text-purple-600" />
          Actions assigned to my Department ({user?.department || 'Non défini'})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {departmentActions?.by_status && Object.keys(departmentActions.by_status).length > 0 ? (
            Object.entries(departmentActions.by_status).map(([status, count]) => (
              <Card 
                key={status} 
                className={`border-2 cursor-pointer hover:shadow-lg transition-shadow ${actionStatusColors[status] || 'bg-gray-100 border-gray-300 text-gray-800'}`}
                onClick={() => handleDepartmentStatusClick(status)}
              >
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold mb-1">{count}</div>
                  <div className="text-sm font-medium">{status}</div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-8">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Aucune action assignée au département</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              Résumé des Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {dashboardData?.actions?.total || 0}
            </div>
            <p className="text-gray-600">Actions assignées au total</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <ClipboardList className="h-5 w-5 mr-2" />
              Résumé des Étapes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600 mb-2">
              {dashboardData?.steps?.total || 0}
            </div>
            <p className="text-gray-600">Étapes assignées au total</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Edit Plan Dialog Component
const EditPlanDialog = ({ plan, onPlanUpdated, open: externalOpen, onClose }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOpen !== undefined ? onClose : setInternalOpen;
  
  const { isSuperUser } = useAuth();
  const [baseTitle, setBaseTitle] = useState(plan?.name?.replace(/\s–\s\d{4}$/, '') || '');
  const [selectedYear, setSelectedYear] = useState(plan?.start_year || new Date().getFullYear());
  const [formData, setFormData] = useState({
    name: plan?.name || '',
    description: plan?.description || '',
    start_year: plan?.start_year || new Date().getFullYear(),
    end_year: plan?.end_year || new Date().getFullYear() + 5,
    organization: plan?.organization || ''
  });
  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState('');

  useEffect(() => {
    if (open) {
      fetchOrganization();
      if (plan) {
        const extractedTitle = plan.name?.replace(/\s–\s\d{4}$/, '') || '';
        setBaseTitle(extractedTitle);
        setSelectedYear(plan.start_year || new Date().getFullYear());
        setFormData({
          name: plan.name || '',
          description: plan.description || '',
          start_year: plan.start_year || new Date().getFullYear(),
          end_year: plan.end_year || new Date().getFullYear() + 5,
          organization: plan.organization || ''
        });
      }
    }
  }, [open, plan]);

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${API}/organization`);
      setOrganization(response.data?.name || 'Nom de l\'Organisation');
    } catch (error) {
      console.error('Error fetching organization:', error);
      setOrganization('Nom de l\'Organisation');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!baseTitle.trim()) {
      alert('Le titre de base est obligatoire');
      return;
    }

    setLoading(true);

    try {
      const finalTitle = `${baseTitle.trim()} – ${selectedYear}`;
      const submitData = {
        ...formData,
        name: finalTitle,
        start_year: selectedYear,
        end_year: selectedYear + 5,
        organization: organization
      };

      const response = await axios.put(`${API}/plans/${plan.id}`, submitData);
      onPlanUpdated(response.data);
      if (externalOpen !== undefined) {
        onClose(false);
      } else {
        setOpen(false);
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      alert('Erreur lors de la mise à jour du plan');
    } finally {
      setLoading(false);
    }
  };

  const yearOptions = [];
  for (let i = new Date().getFullYear() - 5; i <= new Date().getFullYear() + 10; i++) {
    yearOptions.push(i);
  }

  if (!plan || !isSuperUser()) return null;

  // If being used externally (controlled mode), don't render DialogTrigger
  if (externalOpen !== undefined) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le Plan Stratégique</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="base-title">Titre de Base *</Label>
            <Input
              id="base-title"
              value={baseTitle}
              onChange={(e) => setBaseTitle(e.target.value)}
              placeholder="Ex: PST"
              required
            />
          </div>

          <div>
            <Label htmlFor="year">Année</Label>
            <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="final-title">Titre Final</Label>
            <Input
              id="final-title"
              value={baseTitle ? `${baseTitle} – ${selectedYear}` : ''}
              readOnly
              className="bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Le titre final sera généré automatiquement
            </p>
          </div>

          <div>
            <Label htmlFor="organization">Organisation</Label>
            <Input
              id="organization"
              value={organization}
              readOnly
              className="bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Configurable dans l'onglet Administration
            </p>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description du plan stratégique..."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                if (externalOpen !== undefined) {
                  onClose(false);
                } else {
                  setOpen(false);
                }
              }}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading || !baseTitle.trim()}
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
  }

  // Uncontrolled mode with DialogTrigger
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit className="w-4 h-4 mr-2" />
          Modifier Plan
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Modifier le Plan Stratégique</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="base-title">Titre de Base</Label>
            <Input
              id="base-title"
              value={baseTitle}
              onChange={(e) => {
                const value = e.target.value;
                setBaseTitle(value);
                setFormData(prev => ({
                  ...prev,
                  name: `${value.trim()} – ${selectedYear}`
                }));
              }}
              placeholder="Titre du plan..."
              required
            />
          </div>

          <div>
            <Label htmlFor="year">Année de Début</Label>
            <Select 
              value={selectedYear.toString()} 
              onValueChange={(value) => {
                const year = parseInt(value);
                setSelectedYear(year);
                setFormData(prev => ({
                  ...prev,
                  name: `${baseTitle.trim()} – ${year}`,
                  start_year: year,
                  end_year: year + 5
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="organization">Organisation</Label>
            <Input
              id="organization"
              value={organization}
              readOnly
              placeholder="Chargement..."
              className="bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Configurable dans l'onglet Administration
            </p>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description du plan stratégique..."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading || !baseTitle.trim()}
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Create Plan Dialog Component
const CreatePlanDialog = ({ onPlanCreated }) => {
  const [open, setOpen] = useState(false);
  const [baseTitle, setBaseTitle] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_year: new Date().getFullYear(),
    end_year: new Date().getFullYear() + 5,
    organization: ''
  });
  const [fetchingOrganization, setFetchingOrganization] = useState(false);
  const { isModifyUser } = useAuth();

  useEffect(() => {
    if (open) {
      fetchOrganization();
    }
  }, [open]);

  useEffect(() => {
    // Auto-update title when base title or year changes
    if (baseTitle.trim()) {
      const newTitle = `${baseTitle.trim()} – ${selectedYear}`;
      setFormData(prev => ({ ...prev, name: newTitle }));
    }
  }, [baseTitle, selectedYear]);

  const fetchOrganization = async () => {
    try {
      setFetchingOrganization(true);
      const response = await axios.get(`${API}/organization`);
      setFormData(prev => ({ ...prev, organization: response.data.name }));
    } catch (error) {
      console.error('Error fetching organization:', error);
      setFormData(prev => ({ ...prev, organization: 'Organisation non configurée' }));
    } finally {
      setFetchingOrganization(false);
    }
  };

  const handleBaseTitleChange = (value) => {
    setBaseTitle(value);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setFormData(prev => ({ 
      ...prev, 
      start_year: year, 
      end_year: year + 5 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/plans`, formData);
      onPlanCreated(response.data);
      setOpen(false);
      setFormData({
        name: '',
        description: '',
        start_year: new Date().getFullYear(),
        end_year: new Date().getFullYear() + 5,
        organization: ''
      });
      setBaseTitle('');
      setSelectedYear(new Date().getFullYear());
    } catch (error) {
      console.error('Error creating plan:', error);
    }
  };

  // Generate year options (current year - 2 to current year + 10)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = currentYear - 2; i <= currentYear + 10; i++) {
    yearOptions.push(i);
  }

  if (!isModifyUser()) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Plan Stratégique
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Créer un Plan Stratégique</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="base-title">Titre de Base *</Label>
            <Input
              id="base-title"
              value={baseTitle}
              onChange={(e) => handleBaseTitleChange(e.target.value)}
              placeholder="PST"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Le titre final sera: "{baseTitle.trim() || 'Titre'} – {selectedYear}"</p>
          </div>

          <div>
            <Label htmlFor="year">Année *</Label>
            <Select 
              value={selectedYear.toString()} 
              onValueChange={(value) => handleYearChange(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une année" />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="final-title">Titre Final *</Label>
            <Input
              id="final-title"
              value={formData.name}
              readOnly
              className="bg-gray-50 cursor-not-allowed"
              placeholder="Titre généré automatiquement"
            />
          </div>
          
          <div>
            <Label htmlFor="organization">Organisation *</Label>
            <Input
              id="organization"
              value={fetchingOrganization ? 'Chargement...' : formData.organization}
              readOnly
              className="bg-gray-50 cursor-not-allowed"
              placeholder="Auto-rempli depuis la configuration"
            />
            <p className="text-xs text-gray-500 mt-1">
              Organisation configurée dans l'onglet Administration
            </p>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description du plan stratégique..."
              rows={3}
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={fetchingOrganization || !formData.organization || !baseTitle.trim()}
            >
              Créer
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Home Component
const Home = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState({});
  const [organizationLogo, setOrganizationLogo] = useState(null);
  const { user, logout, isSuperUser, isModifyUser } = useAuth();
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get active tab from URL or default to 'accueil'
  const getActiveTabFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('tab') || 'accueil';
  };
  
  // Get sub-tab from URL for PST management
  const getSubTabFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('subtab') || 'gestion';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromURL());
  const [activeSubTab, setActiveSubTab] = useState(getSubTabFromURL());

  // Update URL when tab changes
  const handleTabChange = (tab, subtab = null) => {
    setActiveTab(tab);
    if (subtab) {
      setActiveSubTab(subtab);
    }
    
    const searchParams = new URLSearchParams();
    searchParams.set('tab', tab);
    if (subtab) {
      searchParams.set('subtab', subtab);
    }
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  // Listen for URL changes (browser back/forward)
  useEffect(() => {
    const newTab = getActiveTabFromURL();
    const newSubTab = getSubTabFromURL();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
    if (newSubTab !== activeSubTab) {
      setActiveSubTab(newSubTab);
    }
  }, [location.search]);

  const fetchOrganizationLogo = async () => {
    try {
      const response = await axios.get(`${API}/organization/logo`);
      if (response.data) {
        // Add timestamp to force refresh
        const logoUrl = `${API}/organization/logo/file?t=${Date.now()}`;
        console.log('Setting logo URL:', logoUrl); // Debug log
        setOrganizationLogo(logoUrl);
      } else {
        console.log('No logo data received'); // Debug log
        setOrganizationLogo(null);
      }
    } catch (error) {
      // No logo found, that's okay
      console.log('Error fetching logo:', error); // Debug log
      setOrganizationLogo(null);
    }
  };

  // Refresh logo when switching to different tabs (in case it was updated)
  const handleTabChangeWithLogoRefresh = (tab, subtab = null) => {
    handleTabChange(tab, subtab);
    if (tab === 'administration') {
      // Refresh logo when going to administration tab
      setTimeout(() => fetchOrganizationLogo(), 100);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchOrganizationLogo();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
      
      if (response.data.length > 0 && !selectedPlan) {
        // Check for default PST first
        try {
          const defaultResponse = await axios.get(`${API}/users/me/default-pst`);
          if (defaultResponse.data.default_pst) {
            const defaultPlan = response.data.find(plan => plan.id === defaultResponse.data.default_pst);
            if (defaultPlan) {
              setSelectedPlan(defaultPlan);
              return;
            }
          }
        } catch (error) {
          console.error('Error fetching default PST:', error);
        }
        
        // Fallback to first plan if no default PST
        setSelectedPlan(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanCreated = (newPlan) => {
    setPlans([...plans, newPlan]);
    setSelectedPlan(newPlan);
  };

  const handlePlanUpdated = (updatedPlan) => {
    setPlans(plans.map(plan => plan.id === updatedPlan.id ? updatedPlan : plan));
    setSelectedPlan(updatedPlan);
  };

  const handlePlanDeleted = () => {
    // Refresh the plans list after deletion
    fetchPlans();
  };

  const handleNavigateFromAccueil = (targetTab, filters) => {
    setActiveTab(targetTab);
    setActiveFilters(filters || {});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabsConfig = [
    { value: 'accueil', label: 'Accueil', icon: HomeIcon, show: true },
    { value: 'pst', label: 'PST', icon: Flag, show: true },
    { value: 'dashboard', label: 'Vue d\'ensemble', icon: BarChart3, show: true },
    { value: 'actions', label: 'Actions', icon: Target, show: true },
    { value: 'mes-actions', label: 'Mes Actions', icon: User, show: true },
    { value: 'mes-activites', label: 'Mes activités', icon: ClipboardList, show: true },
    { value: 'gerer-psts', label: 'Gérer les PSTs', icon: Settings, show: isSuperUser() },
    { value: 'documents', label: 'Documents', icon: FileText, show: isSuperUser() },
    { value: 'administration', label: 'Administration', icon: Shield, show: isSuperUser() },
    { value: 'logs', label: 'Logs', icon: Clock, show: isSuperUser() }
  ].filter(tab => tab.show);

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Sidebar - Fixed */}
      <div className="w-64 bg-white shadow-lg border-r fixed left-0 top-0 h-full z-10">
        {/* Logo/Header */}
        <div className="p-6 border-b">
          <div className="flex flex-col items-center space-y-4">
            {/* Organization Logo */}
            {organizationLogo && (
              <div className="w-20 h-20 flex items-center justify-center bg-gray-50 rounded-lg border">
                <img 
                  src={organizationLogo} 
                  alt="Logo Organisation" 
                  className="max-w-16 max-h-16 object-contain"
                  onError={(e) => {
                    console.log('Logo failed to load:', organizationLogo, e);
                    setOrganizationLogo(null);
                  }}
                  onLoad={() => console.log('Logo loaded successfully:', organizationLogo)}
                />
              </div>
            )}
            
            {/* App Title */}
            <div className="flex flex-col items-center space-y-1">
              <div className="flex items-center space-x-2">
                <Building2 className="h-6 w-6 text-blue-600" />
                <h1 className="text-lg font-bold text-gray-900">PST Manager</h1>
              </div>
              <p className="text-xs text-gray-600 text-center">Plan Stratégique Transversal</p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="mt-6 px-3">
            <div className="space-y-2">
              {tabsConfig.map((tab) => {
                const isActive = activeTab === tab.value;
                const Icon = tab.icon;
                
                return (
                  <button
                    key={tab.value}
                    onClick={() => handleTabChangeWithLogoRefresh(tab.value)}
                    className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 ${
                      isActive
                        ? tab.value === 'administration'
                          ? 'bg-red-50 text-red-700 border-l-4 border-red-500 shadow-sm'
                          : 'bg-blue-50 text-blue-700 border-l-4 border-blue-500 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-4 border-transparent'
                    }`}
                  >
                    <Icon className={`h-5 w-5 mr-3 ${
                      isActive
                        ? tab.value === 'administration'
                          ? 'text-red-600'
                          : 'text-blue-600'
                        : 'text-gray-400'
                    }`} />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* User Info at Bottom */}
          <div className="absolute bottom-0 w-64 p-4 border-t bg-white">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-600">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.role}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
            {/* Default PST Setting */}
            <DefaultPSTDialog 
              user={user} 
              plans={plans}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
            />
          </div>
        </div>

        {/* Main Content Area - Offset for fixed sidebar */}
        <div className="flex-1 bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col ml-64">
          {/* Content Header - Fixed */}
          <div className="bg-white shadow-sm border-b fixed top-0 right-0 z-5" style={{left: '256px'}}>
            <div className="px-6 py-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {tabsConfig.find(tab => tab.value === activeTab)?.label}
              </h2>
              {/* Only show plan info for PST-specific tabs */}
              {selectedPlan && ['pst', 'dashboard', 'actions'].includes(activeTab) && (
                <p className="text-sm text-gray-600 mt-1">
                  {selectedPlan.name} ({selectedPlan.start_year}-{selectedPlan.end_year})
                </p>
              )}
            </div>
          </div>

          {/* Content - Offset for fixed header */}
          <main className="flex-1 p-6 overflow-auto" style={{paddingTop: '120px'}}>
            <div className="max-w-7xl mx-auto">
              {/* Show welcome screen only if no plan selected AND not on global tabs */}
              {!selectedPlan && !['accueil', 'mes-activites','gerer-psts','documents','administration','logs'].includes(activeTab) ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-6" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Bienvenue dans PST Manager</h2>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Créez et gérez vos plans stratégiques transversaux pour communes et CPAS. 
                      Suivez vos objectifs, indicateurs et actions en temps réel.
                    </p>
                    <CreatePlanDialog onPlanCreated={handlePlanCreated} />
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {/* Global tabs - always available */}
                  {activeTab === 'accueil' && (
                    <div className="space-y-6">
                      <Accueil onNavigate={handleNavigateFromAccueil} />
                    </div>
                  )}

                  {activeTab === 'mes-activites' && (
                    <div className="space-y-6">
                      <MesActivites filters={activeFilters} />
                    </div>
                  )}

                  {/* PST-specific tabs - require selected plan */}
                  {selectedPlan && activeTab === 'dashboard' && (
                    <div className="space-y-6">
                      <Dashboard plan={selectedPlan} onNavigate={handleNavigateFromAccueil} user={user} />
                    </div>
                  )}
                  
                  {selectedPlan && activeTab === 'pst' && (
                    <div className="space-y-6">
                      {/* PST Management Section */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Flag className="h-5 w-5 text-purple-600" />
                          </div>
                          <h2 className="text-2xl font-bold text-gray-900">Plan Stratégique Transversal</h2>
                        </div>
                        <div className="flex items-center space-x-4">
                          {selectedPlan && (
                            <Select 
                              value={selectedPlan.id} 
                              onValueChange={(value) => {
                                const plan = plans.find(p => p.id === value);
                                setSelectedPlan(plan);
                              }}
                            >
                              <SelectTrigger className="w-64">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {plans.map((plan) => (
                                  <SelectItem key={plan.id} value={plan.id}>
                                    {plan.name} ({plan.start_year}-{plan.end_year})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <div className="flex items-center space-x-2">
                            {isSuperUser() && selectedPlan && (
                              <EditPlanDialog plan={selectedPlan} onPlanUpdated={handlePlanUpdated} />
                            )}
                            {isSuperUser() && !selectedPlan && (
                              <span className="text-xs text-gray-500">No plan selected</span>
                            )}
                            <CreatePlanDialog onPlanCreated={handlePlanCreated} />
                          </div>
                        </div>
                      </div>
                      
                      {/* Separator between PST and Visions sections */}
                      <div className="flex items-center my-8">
                        <div className="flex-grow border-t border-gray-200"></div>
                        <div className="flex items-center mx-4">
                          <div className="w-2 h-2 bg-gray-300 rounded-full mx-1"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full mx-1"></div>
                          <div className="w-2 h-2 bg-gray-300 rounded-full mx-1"></div>
                        </div>
                        <div className="flex-grow border-t border-gray-200"></div>
                      </div>
                      
                      <VisionsList plan={selectedPlan} />
                    </div>
                  )}

                  {selectedPlan && activeTab === 'actions' && (
                    <div className="space-y-6">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Activity className="h-5 w-5 text-orange-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">Actions</h2>
                      </div>
                      <ActionsTab plan={selectedPlan} filters={activeFilters} />
                    </div>
                  )}

                  {activeTab === 'mes-actions' && (
                    <div className="space-y-6">
                      <MesActions filters={activeFilters} />
                    </div>
                  )}

                  {isSuperUser() && activeTab === 'gerer-psts' && (
                    <div className="space-y-6">
                      <GererPSTs 
                        plans={plans} 
                        onPlanCreated={handlePlanCreated} 
                        onPlanUpdated={handlePlanUpdated}
                        onPlanDeleted={handlePlanDeleted}
                      />
                    </div>
                  )}

                  {isSuperUser() && activeTab === 'documents' && (
                    <div className="space-y-6">
                      <Documents />
                    </div>
                  )}

                  {isSuperUser() && activeTab === 'administration' && (
                    <div className="space-y-6">
                      <Administration />
                    </div>
                  )}

                  {isSuperUser() && activeTab === 'logs' && (
                    <div className="space-y-6">
                      <Logs />
                    </div>
                  )}

                  {/* Show message for PST-specific tabs when no plan selected */}
                  {!selectedPlan && ['dashboard', 'pst', 'actions'].includes(activeTab) && (
                    <Card>
                      <CardContent className="p-12 text-center">
                        <Target className="h-16 w-16 text-gray-400 mx-auto mb-6" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Aucun Plan Sélectionné</h2>
                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                          Veuillez sélectionner ou créer un plan stratégique pour accéder à cette section.
                        </p>
                        <CreatePlanDialog onPlanCreated={handlePlanCreated} />
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return token ? children : <Navigate to="/login" replace />;
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
};

// Actions Tab Component
const ActionsTab = ({ plan, filters: externalFilters = {} }) => {
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredActions, setFilteredActions] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    department: 'all',
    status: externalFilters.statusFilter || 'all',
    rag_status: 'all',
    search: ''
  });
  const { isModifyUser } = useAuth();

  useEffect(() => {
    fetchPlanActions();
    fetchUsers();
  }, [externalFilters.planFilter]); // Re-fetch when planFilter changes

  useEffect(() => {
    // Apply external filters when they change
    if (externalFilters.statusFilter) {
      setLocalFilters(prev => ({ ...prev, status: externalFilters.statusFilter }));
    }
    if (externalFilters.departmentFilter) {
      setLocalFilters(prev => ({ ...prev, department: externalFilters.departmentFilter }));
    }
  }, [externalFilters]);

  useEffect(() => {
    applyFilters();
  }, [actions, localFilters]);

  const fetchPlanActions = async () => {
    try {
      setLoading(true);
      let response;
      
      // If planFilter is provided, fetch actions for specific plan
      if (externalFilters.planFilter) {
        response = await axios.get(`${API}/plans/${externalFilters.planFilter}/actions`);
      } else {
        // Use the all actions endpoint to get actions across all plans
        // This ensures we show actions with proper Voir/Modifier buttons
        response = await axios.get(`${API}/actions/all`);
      }
      
      setActions(response.data);
    } catch (error) {
      console.error('Error fetching actions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleViewAction = (action) => {
    setSelectedAction(action);
    setViewMode('detail');
  };

  const handleEditAction = (action) => {
    setSelectedAction(action);
    setEditDialogOpen(true);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedAction(null);
  };

  const handleActionUpdated = (updatedAction) => {
    setActions(actions.map(action => 
      action.id === updatedAction.id ? updatedAction : action
    ));
    setEditDialogOpen(false);
  };

  const applyFilters = () => {
    let filtered = [...actions];

    if (localFilters.department && localFilters.department !== 'all') {
      filtered = filtered.filter(action => action.department === localFilters.department);
    }

    if (localFilters.status && localFilters.status !== 'all') {
      filtered = filtered.filter(action => action.status === localFilters.status);
    }

    if (localFilters.rag_status && localFilters.rag_status !== 'all') {
      filtered = filtered.filter(action => action.rag_status === localFilters.rag_status);
    }

    if (localFilters.search) {
      const searchLower = localFilters.search.toLowerCase();
      filtered = filtered.filter(action => 
        action.title.toLowerCase().includes(searchLower) ||
        (action.description && action.description.toLowerCase().includes(searchLower))
      );
    }

    setFilteredActions(filtered);
  };

  const getUniqueValues = (key) => {
    return [...new Set(actions.map(action => action[key]).filter(Boolean))].sort();
  };

  const getUsersByIds = (userIds) => {
    return users.filter(user => userIds.includes(user.id));
  };

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'Non Démarré': { color: 'bg-slate-100 text-slate-800' },
      'A l\'étude': { color: 'bg-purple-100 text-purple-800' },
      'Planifié': { color: 'bg-yellow-100 text-yellow-800' },
      'En Cours': { color: 'bg-blue-100 text-blue-800' },
      'Bloqué': { color: 'bg-red-100 text-red-800' },
      'Abandonné': { color: 'bg-gray-100 text-gray-800' },
      'Terminé': { color: 'bg-green-100 text-green-800' }
    };

    const config = statusConfig[status] || statusConfig['A l\'étude'];

    return (
      <Badge className={config.color}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show detail view if selected
  if (viewMode === 'detail' && selectedAction) {
    return <ViewActionPage action={selectedAction} users={users} onBack={handleBackToList} />;
  }

  return (
    <div className="space-y-6">
      {/* Edit Action Dialog */}
      {selectedAction && (
        <EditActionDialog 
          action={selectedAction}
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onActionUpdated={handleActionUpdated}
          users={users}
        />
      )}
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label>Rechercher</Label>
              <Input
                placeholder="Rechercher une action..."
                value={localFilters.search}
                onChange={(e) => setLocalFilters({ ...localFilters, search: e.target.value })}
              />
            </div>
            <div>
              <Label>Département</Label>
              <Select 
                value={localFilters.department} 
                onValueChange={(value) => setLocalFilters({ ...localFilters, department: value === 'all' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les départements" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les départements</SelectItem>
                  {getUniqueValues('department').map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status Action</Label>
              <Select 
                value={localFilters.status} 
                onValueChange={(value) => setLocalFilters({ ...localFilters, status: value === 'all' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les status</SelectItem>
                  <SelectItem value="Non Démarré">Non Démarré</SelectItem>
                  <SelectItem value="A l'étude">A l'étude</SelectItem>
                  <SelectItem value="Planifié">Planifié</SelectItem>
                  <SelectItem value="En Cours">En Cours</SelectItem>
                  <SelectItem value="Bloqué">Bloqué</SelectItem>
                  <SelectItem value="Abandonné">Abandonné</SelectItem>
                  <SelectItem value="Terminé">Terminé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>RAG Status</Label>
              <Select 
                value={localFilters.rag_status} 
                onValueChange={(value) => setLocalFilters({ ...localFilters, rag_status: value === 'all' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les RAG" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les RAG</SelectItem>
                  <SelectItem value="green">On Track</SelectItem>
                  <SelectItem value="amber">At Risk</SelectItem>
                  <SelectItem value="red">Off Track</SelectItem>
                  <SelectItem value="grey">Not Started</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => setLocalFilters({ department: 'all', status: 'all', rag_status: 'all', search: '' })}
                className="w-full"
              >
                Réinitialiser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{actions.length}</div>
            <div className="text-sm text-gray-600">Total Actions</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {actions.filter(a => a.status === 'Terminé').length}
            </div>
            <div className="text-sm text-gray-600">Terminées</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {actions.filter(a => a.status === 'En Cours').length}
            </div>
            <div className="text-sm text-gray-600">En Cours</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-600">
              {actions.filter(a => a.status === 'Bloqué').length}
            </div>
            <div className="text-sm text-gray-600">Bloquées</div>
          </CardContent>
        </Card>
      </div>

      {/* Actions List */}
      {filteredActions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Activity className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {actions.length === 0 ? 'Aucune Action' : 'Aucune Action correspondante'}
            </h3>
            <p className="text-gray-600">
              {actions.length === 0 
                ? 'Les actions créées dans les objectifs opérationnels apparaîtront ici'
                : 'Aucune action ne correspond aux critères de filtrage sélectionnés'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredActions.map((action, index) => (
            <Card key={action.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          <span className="font-bold text-orange-600 text-sm">A{index + 1}</span>
                        </div>
                        <h4 className="font-semibold text-lg">{action.title}</h4>
                      </div>
                      <div className="flex items-center space-x-2">
                        <StatusBadge status={action.status} />
                        <RAGBadge status={action.rag_status} />
                      </div>
                    </div>
                    {action.description && (
                      <p className="text-gray-600 text-sm mb-3 ml-11">{action.description}</p>
                    )}
                    
                    {/* Step Count */}
                    <div className="flex items-center space-x-2 ml-11 mb-3">
                      <ClipboardList className="h-4 w-4 text-gray-400" />
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Étapes:</span>{' '}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          (action.step_count || 0) > 0 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {action.step_count || 0} étape{(action.step_count || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    
                    {/* Participants */}
                    {action.participants && action.participants.length > 0 && (
                      <div className="flex items-center space-x-2 ml-11 mb-3">
                        <Users className="h-4 w-4 text-gray-400" />
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Participants:</span>{' '}
                          {getUsersByIds(action.participants).length > 0 ? (
                            getUsersByIds(action.participants).length > 3 ? (
                              <>
                                {getUsersByIds(action.participants).slice(0, 3).map(user => user.name).join(', ')}
                                <span className="text-gray-500"> +{getUsersByIds(action.participants).length - 3} autres</span>
                              </>
                            ) : (
                              getUsersByIds(action.participants).map(user => user.name).join(', ')
                            )
                          ) : (
                            <span className="text-gray-400">Utilisateurs non trouvés</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Context Information */}
                    {action.context && (
                      <div className="ml-11 mb-3">
                        <div className="text-xs text-gray-500 space-y-1">
                          <div className="flex items-center space-x-2">
                            <Flag className="h-3 w-3" />
                            <span>{action.context.vision_title}</span>
                            <span>→</span>
                            <span className={`${action.context.panel_type === 'externe' ? 'text-green-600' : 'text-blue-600'}`}>
                              {action.context.panel_title}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Target className="h-3 w-3" />
                            <span>{action.context.strategic_objective_code}: {action.context.strategic_objective_title}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-3 w-3" />
                            <span>{action.context.operational_objective_code}: {action.context.operational_objective_title}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Progression</span>
                      <span className="font-medium">{Math.round(action.progress_percentage)}%</span>
                    </div>
                    <Progress value={action.progress_percentage} className="h-2" />
                  </div>

                  {/* Dates */}
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">Période</div>
                    <div className="text-sm">
                      {action.start_date && action.end_date ? (
                        <span>
                          {new Date(action.start_date).toLocaleDateString('fr-FR')} - {new Date(action.end_date).toLocaleDateString('fr-FR')}
                        </span>
                      ) : (
                        <span className="text-gray-400">Non définie</span>
                      )}
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">Budget</div>
                    <div className="text-sm">
                      {action.budget_planned ? (
                        <span className="font-medium">
                          {action.budget_planned.toLocaleString('fr-FR')} €
                          {action.budget_actual && (
                            <span className="text-gray-500 ml-1">
                              (Réel: {action.budget_actual.toLocaleString('fr-FR')} €)
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400">Non défini</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    {action.department && (
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 mr-1" />
                        {action.department}
                      </div>
                    )}
                    {action.participants && action.participants.length > 0 && (
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        {getUsersByIds(action.participants).length} participant(s)
                      </div>
                    )}
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      Créé le {new Date(action.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewAction(action)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Voir cette action"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {isModifyUser() && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditAction(action)}
                        className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                        title="Modifier cette action"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Edit Action Dialog Component
const EditActionDialog = ({ action, open, onClose, onActionUpdated, users }) => {
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    title: action?.title || '',
    description: action?.description || '',
    status: action?.status || 'A l\'étude',
    department: action?.department || '',
    participants: action?.participants || [],
    start_date: action?.start_date ? new Date(action.start_date).toISOString().split('T')[0] : '',
    end_date: action?.end_date ? new Date(action.end_date).toISOString().split('T')[0] : '',
    budget_planned: action?.budget_planned || ''
  });
  const [loading, setLoading] = useState(false);

  const statusOptions = [
    'Non Démarré',
    'A l\'étude', 
    'Planifié',
    'En Cours',
    'Bloqué',
    'Abandonné',
    'Terminé'
  ];

  useEffect(() => {
    if (open) {
      fetchDepartments();
      // Reset form data when action changes
      setFormData({
        title: action?.title || '',
        description: action?.description || '',
        status: action?.status || 'A l\'étude',
        department: action?.department || '',
        participants: action?.participants || [],
        start_date: action?.start_date ? new Date(action.start_date).toISOString().split('T')[0] : '',
        end_date: action?.end_date ? new Date(action.end_date).toISOString().split('T')[0] : '',
        budget_planned: action?.budget_planned || ''
      });
    }
  }, [open, action]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleParticipantToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.includes(userId)
        ? prev.participants.filter(id => id !== userId)
        : [...prev.participants, userId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Le titre de l\'action est obligatoire');
      return;
    }

    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Convert dates to proper format
      if (submitData.start_date) {
        submitData.start_date = new Date(submitData.start_date).toISOString();
      }
      if (submitData.end_date) {
        submitData.end_date = new Date(submitData.end_date).toISOString();
      }
      
      // Convert budget to number
      if (submitData.budget_planned) {
        submitData.budget_planned = parseFloat(submitData.budget_planned);
      }

      const response = await axios.put(`${API}/actions/${action.id}`, submitData);
      onActionUpdated(response.data);
      onClose();
    } catch (error) {
      console.error('Error updating action:', error);
      alert('Erreur lors de la modification de l\'action');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifier l'Action</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="action-title">Titre de l'Action *</Label>
            <Input
              id="action-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Formation du personnel d'accueil"
              required
            />
          </div>

          <div>
            <Label htmlFor="action-description">Description</Label>
            <Textarea
              id="action-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description détaillée de l'action à réaliser..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="action-status">Status</Label>
            <Select 
              value={formData.status} 
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="action-department">Département</Label>
            <Select 
              value={formData.department} 
              onValueChange={(value) => setFormData({ ...formData, department: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un département" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.name}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Participants</Label>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto">
              {users.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun utilisateur disponible</p>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`user-${user.id}`}
                        checked={formData.participants.includes(user.id)}
                        onChange={() => handleParticipantToggle(user.id)}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor={`user-${user.id}`} className="text-sm flex-1 flex items-center justify-between">
                        <span>{user.name}</span>
                        <span className="text-xs text-gray-500">{user.email}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {formData.participants.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                {formData.participants.length} participant(s) sélectionné(s)
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start-date">Date de Début</Label>
              <Input
                id="start-date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="end-date">Date de Fin</Label>
              <Input
                id="end-date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="budget">Budget Prévu (€)</Label>
            <Input
              id="budget"
              type="number"
              value={formData.budget_planned}
              onChange={(e) => setFormData({ ...formData, budget_planned: e.target.value })}
              placeholder="15000"
              min="0"
              step="0.01"
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={loading}
            >
              {loading ? 'Modification...' : 'Modifier Action'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// View Action Page Component
const ViewActionPage = ({ action, users, onBack }) => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createStepOpen, setCreateStepOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [editStepOpen, setEditStepOpen] = useState(false);
  const { isModifyUser } = useAuth();

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'Non Démarré': { color: 'bg-slate-100 text-slate-800' },
      'A l\'étude': { color: 'bg-purple-100 text-purple-800' },
      'Planifié': { color: 'bg-yellow-100 text-yellow-800' },
      'En Cours': { color: 'bg-blue-100 text-blue-800' },
      'Bloqué': { color: 'bg-red-100 text-red-800' },
      'Abandonné': { color: 'bg-gray-100 text-gray-800' },
      'Terminé': { color: 'bg-green-100 text-green-800' }
    };

    const config = statusConfig[status] || statusConfig['A l\'étude'];

    return (
      <Badge className={config.color}>
        {status}
      </Badge>
    );
  };

  useEffect(() => {
    fetchSteps();
  }, [action.id]);

  const fetchSteps = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/actions/${action.id}/steps`);
      setSteps(response.data);
    } catch (error) {
      console.error('Error fetching steps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStepCreated = (newStep) => {
    setSteps([...steps, newStep]);
    setCreateStepOpen(false);
  };

  const handleEditStep = (step) => {
    setSelectedStep(step);
    setEditStepOpen(true);
  };

  const handleStepUpdated = (updatedStep) => {
    setSteps(steps.map(step => step.id === updatedStep.id ? updatedStep : step));
    setEditStepOpen(false);
    setSelectedStep(null);
  };

  const getUsersByIds = (userIds) => {
    return users.filter(user => userIds.includes(user.id));
  };

  const calculateDaysDifference = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Retour aux Actions
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{action.title}</h1>
            <p className="text-gray-600 mt-1">{action.description}</p>
          </div>
        </div>
        {isModifyUser() && (
          <Button
            onClick={() => setCreateStepOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Créer une Étape
          </Button>
        )}
      </div>

      {/* Action Details */}
      <Card>
        <CardHeader>
          <CardTitle>Détails de l'Action</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label className="text-sm font-medium text-gray-600">Status</Label>
              <div className="mt-1">
                <StatusBadge status={action.status}  />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Département</Label>
              <p className="mt-1 text-sm">{action.department || 'Non défini'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Budget Prévu</Label>
              <p className="mt-1 text-sm">
                {action.budget_planned ? `${action.budget_planned.toLocaleString('fr-FR')} €` : 'Non défini'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Target className="h-5 w-5 mr-2" />
            Étapes de l'Action
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : steps.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune Étape</h3>
              <p className="text-gray-600 mb-4">Cette action n'a pas encore d'étapes définies.</p>
              {isModifyUser() && (
                <Button
                  onClick={() => setCreateStepOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer la première Étape
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {steps.map((step, index) => (
                <Card key={step.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-blue-600">{index + 1}</span>
                          </div>
                          <h4 className="font-semibold text-lg">{step.title}</h4>
                          <div className="flex items-center space-x-2">
                            {/* Step Status Badge */}
                            <Badge className={
                              step.status === 'Terminé' ? 'bg-green-100 text-green-800' :
                              step.status === 'En Cours' ? 'bg-blue-100 text-blue-800' :
                              step.status === 'Bloqué' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {step.status}
                            </Badge>
                            {/* RAG Status Badge */}
                            <Badge className={
                              step.rag_status === 'green' ? 'bg-emerald-500 text-white' :
                              step.rag_status === 'amber' ? 'bg-amber-500 text-white' :
                              step.rag_status === 'red' ? 'bg-red-500 text-white' :
                              'bg-slate-400 text-white'
                            }>
                              <div className="flex items-center">
                                <div className={`w-2 h-2 rounded-full mr-1 ${
                                  step.rag_status === 'green' ? 'bg-white' :
                                  step.rag_status === 'amber' ? 'bg-white' :
                                  step.rag_status === 'red' ? 'bg-white' :
                                  'bg-white'
                                }`}></div>
                                {step.rag_status === 'green' ? 'Sur la Bonne Voie' :
                                 step.rag_status === 'amber' ? 'À Risque' :
                                 step.rag_status === 'red' ? 'Hors Piste' :
                                 'Non Démarré'}
                              </div>
                            </Badge>
                          </div>
                        </div>
                      </div>
                      {isModifyUser() && (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditStep(step)}
                            className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                            title="Modifier cette étape"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {step.description && (
                      <p className="text-gray-600 text-sm mb-3 ml-9">{step.description}</p>
                    )}
                    
                    <div className="ml-9 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <Label className="text-xs font-medium text-gray-600">Date de Début</Label>
                            <p className="mt-1">
                              {step.start_date ? new Date(step.start_date).toLocaleDateString('fr-FR') : 'Non définie'}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs font-medium text-gray-600">Date de Fin</Label>
                            <p className="mt-1">
                              {step.end_date ? new Date(step.end_date).toLocaleDateString('fr-FR') : 'Non définie'}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs font-medium text-gray-600">Durée</Label>
                            <p className="mt-1">
                              {calculateDaysDifference(step.start_date, step.end_date) 
                                ? `${calculateDaysDifference(step.start_date, step.end_date)} jour(s)`
                                : 'Non calculée'
                              }
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs font-medium text-gray-600">Participants</Label>
                            <div className="mt-1">
                              {step.participants && step.participants.length > 0 ? (
                                <div className="text-xs">
                                  {getUsersByIds(step.participants).length > 2 ? (
                                    <>
                                      {getUsersByIds(step.participants).slice(0, 2).map(user => user.name).join(', ')}
                                      <span className="text-gray-500"> +{getUsersByIds(step.participants).length - 2}</span>
                                    </>
                                  ) : (
                                    getUsersByIds(step.participants).map(user => user.name).join(', ')
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">Aucun participant</span>
                              )}
                            </div>
                          </div>
                        </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Step Dialog */}
      <CreateStepDialog 
        actionId={action.id}
        open={createStepOpen}
        onClose={() => setCreateStepOpen(false)}
        onStepCreated={handleStepCreated}
        users={users}
      />

      {/* Edit Step Dialog */}
      {selectedStep && (
        <EditStepDialog 
          step={selectedStep}
          open={editStepOpen}
          onOpenChange={setEditStepOpen}
          onStepUpdated={handleStepUpdated}
          users={users}
        />
      )}
    </div>
  );
};

// Create Step Dialog Component
const CreateStepDialog = ({ actionId, open, onClose, onStepCreated, users }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    participants: [],
    status: 'Non Démarré',
    rag_status: 'grey'
  });
  const [loading, setLoading] = useState(false);

  const handleParticipantToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.includes(userId)
        ? prev.participants.filter(id => id !== userId)
        : [...prev.participants, userId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Le titre de l\'étape est obligatoire');
      return;
    }

    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Convert dates to proper format
      if (submitData.start_date) {
        submitData.start_date = new Date(submitData.start_date).toISOString();
      }
      if (submitData.end_date) {
        submitData.end_date = new Date(submitData.end_date).toISOString();
      }

      const response = await axios.post(`${API}/actions/${actionId}/steps`, submitData);
      onStepCreated(response.data);
      setFormData({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        participants: []
      });
    } catch (error) {
      console.error('Error creating step:', error);
      alert('Erreur lors de la création de l\'étape');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Créer une Étape</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="step-title">Titre de l'Étape *</Label>
            <Input
              id="step-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Analyse des besoins"
              required
            />
          </div>

          <div>
            <Label htmlFor="step-description">Description</Label>
            <Textarea
              id="step-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description détaillée de l'étape..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="step-status">Status de l'Étape</Label>
            <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Non Démarré">Non Démarré</SelectItem>
                <SelectItem value="En Cours">En Cours</SelectItem>
                <SelectItem value="Terminé">Terminé</SelectItem>
                <SelectItem value="Bloqué">Bloqué</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="step-rag-status">Status RAG</Label>
            <Select value={formData.rag_status} onValueChange={(value) => setFormData({ ...formData, rag_status: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un status RAG" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grey">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-slate-400 rounded-full mr-2"></div>
                    Non Démarré
                  </div>
                </SelectItem>
                <SelectItem value="green">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2"></div>
                    Sur la Bonne Voie
                  </div>
                </SelectItem>
                <SelectItem value="amber">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                    À Risque
                  </div>
                </SelectItem>
                <SelectItem value="red">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                    Hors Piste
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="step-start-date">Date de Début</Label>
              <Input
                id="step-start-date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="step-end-date">Date de Fin</Label>
              <Input
                id="step-end-date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label>Participants</Label>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto">
              {users.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun utilisateur disponible</p>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`step-user-${user.id}`}
                        checked={formData.participants.includes(user.id)}
                        onChange={() => handleParticipantToggle(user.id)}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor={`step-user-${user.id}`} className="text-sm flex-1 flex items-center justify-between">
                        <span>{user.name}</span>
                        <span className="text-xs text-gray-500">{user.email}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {formData.participants.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                {formData.participants.length} participant(s) sélectionné(s)
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Création...' : 'Créer Étape'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Mes Activités Component
// Edit Step Dialog Component
const EditStepDialog = ({ step, open, onOpenChange, onStepUpdated, users }) => {
  const [formData, setFormData] = useState({
    title: step?.title || '',
    description: step?.description || '',
    start_date: step?.start_date ? step.start_date.split('T')[0] : '',
    end_date: step?.end_date ? step.end_date.split('T')[0] : '',
    participants: step?.participants || [],
    status: step?.status || 'Non Démarré',
    rag_status: step?.rag_status || 'grey'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (step) {
      setFormData({
        title: step.title || '',
        description: step.description || '',
        start_date: step.start_date ? step.start_date.split('T')[0] : '',
        end_date: step.end_date ? step.end_date.split('T')[0] : '',
        participants: step.participants || [],
        status: step.status || 'Non Démarré',
        rag_status: step.rag_status || 'grey'
      });
    }
  }, [step]);

  const handleParticipantToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.includes(userId)
        ? prev.participants.filter(id => id !== userId)
        : [...prev.participants, userId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Le titre de l\'étape est obligatoire');
      return;
    }

    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Convert dates to proper format
      if (submitData.start_date) {
        submitData.start_date = new Date(submitData.start_date).toISOString();
      }
      if (submitData.end_date) {
        submitData.end_date = new Date(submitData.end_date).toISOString();
      }

      const response = await axios.put(`${API}/steps/${step.id}`, submitData);
      onStepUpdated(response.data);
    } catch (error) {
      console.error('Error updating step:', error);
      alert('Erreur lors de la mise à jour de l\'étape');
    } finally {
      setLoading(false);
    }
  };

  if (!step) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifier l'Étape</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="step-title">Titre de l'Étape *</Label>
            <Input
              id="step-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Titre de l'étape"
              required
            />
          </div>

          <div>
            <Label htmlFor="step-description">Description</Label>
            <Textarea
              id="step-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description détaillée de l'étape..."
              rows={3}
            />
          </div>
          <div>
            <Label htmlFor="step-status">Status de l'Étape</Label>
            <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Non Démarré">Non Démarré</SelectItem>
                <SelectItem value="En Cours">En Cours</SelectItem>
                <SelectItem value="Terminé">Terminé</SelectItem>
                <SelectItem value="Bloqué">Bloqué</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="step-rag-status">Status RAG</Label>
            <Select value={formData.rag_status} onValueChange={(value) => setFormData({ ...formData, rag_status: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un status RAG" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grey">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-slate-400 rounded-full mr-2"></div>
                    Non Démarré
                  </div>
                </SelectItem>
                <SelectItem value="green">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2"></div>
                    Sur la Bonne Voie
                  </div>
                </SelectItem>
                <SelectItem value="amber">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                    À Risque
                  </div>
                </SelectItem>
                <SelectItem value="red">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                    Hors Piste
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="step-start-date">Date de Début</Label>
              <Input
                id="step-start-date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="step-end-date">Date de Fin</Label>
              <Input
                id="step-end-date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label>Participants</Label>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto">
              {users.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun utilisateur disponible</p>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`step-user-${user.id}`}
                        checked={formData.participants.includes(user.id)}
                        onChange={() => handleParticipantToggle(user.id)}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor={`step-user-${user.id}`} className="text-sm flex-1 flex items-center justify-between">
                        <span>{user.name}</span>
                        <span className="text-xs text-gray-500">{user.email}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {formData.participants.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                {formData.participants.length} participant(s) sélectionné(s)
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Mes Actions Component (User-assigned actions only)
const MesActions = ({ filters: externalFilters = {} }) => {
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredActions, setFilteredActions] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    department: 'all',
    status: externalFilters.statusFilter || 'all',
    search: ''
  });

  useEffect(() => {
    fetchMyActions();
    fetchUsers();
  }, []);

  useEffect(() => {
    // Apply external filters when they change
    if (externalFilters.statusFilter) {
      setLocalFilters(prev => ({ ...prev, status: externalFilters.statusFilter }));
    }
    if (externalFilters.departmentFilter) {
      setLocalFilters(prev => ({ ...prev, department: externalFilters.departmentFilter }));
    }
  }, [externalFilters]);

  useEffect(() => {
    applyFilters();
  }, [actions, localFilters]);

  const fetchMyActions = async () => {
    try {
      setLoading(true);
      // Use user-specific actions endpoint
      const response = await axios.get(`${API}/users/me/actions`);
      setActions(response.data);
    } catch (error) {
      console.error('Error fetching my actions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleViewAction = (action) => {
    setSelectedAction(action);
    setViewMode('detail');
  };

  const handleEditAction = (action) => {
    setSelectedAction(action);
    setEditDialogOpen(true);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedAction(null);
  };

  const handleActionUpdated = (updatedAction) => {
    setActions(actions.map(action => 
      action.id === updatedAction.id ? updatedAction : action
    ));
    setEditDialogOpen(false);
  };

  const applyFilters = () => {
    let filtered = [...actions];

    if (localFilters.department && localFilters.department !== 'all') {
      if (localFilters.department === 'unassigned') {
        // Filter for actions without departments (null, empty, or undefined)
        filtered = filtered.filter(action => !action.department || action.department.trim() === '');
      } else {
        // Filter for specific department
        filtered = filtered.filter(action => action.department === localFilters.department);
      }
    }

    if (localFilters.status && localFilters.status !== 'all') {
      filtered = filtered.filter(action => action.status === localFilters.status);
    }

    if (localFilters.search) {
      const searchLower = localFilters.search.toLowerCase();
      filtered = filtered.filter(action => 
        action.title.toLowerCase().includes(searchLower) ||
        (action.description && action.description.toLowerCase().includes(searchLower))
      );
    }

    setFilteredActions(filtered);
  };

  const getUniqueValues = (key) => {
    return [...new Set(actions.map(action => action[key]).filter(Boolean))].sort();
  };

  const getUsersByIds = (userIds) => {
    return users.filter(user => userIds.includes(user.id));
  };

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'Non Démarré': { color: 'bg-slate-100 text-slate-800' },
      'A l\'étude': { color: 'bg-purple-100 text-purple-800' },
      'Planifié': { color: 'bg-yellow-100 text-yellow-800' },
      'En Cours': { color: 'bg-blue-100 text-blue-800' },
      'Bloqué': { color: 'bg-red-100 text-red-800' },
      'Abandonné': { color: 'bg-gray-100 text-gray-800' },
      'Terminé': { color: 'bg-green-100 text-green-800' }
    };

    const config = statusConfig[status] || statusConfig['A l\'étude'];

    return (
      <Badge className={config.color}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show detail view if selected
  if (viewMode === 'detail' && selectedAction) {
    return <ViewActionPage action={selectedAction} users={users} onBack={handleBackToList} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mes Actions</h1>
          <p className="text-gray-600 mt-1">Actions qui me sont assignées</p>
        </div>
        <div className="text-sm text-gray-600">
          {filteredActions.length} action(s) sur {actions.length}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Rechercher</Label>
              <Input
                placeholder="Rechercher une action..."
                value={localFilters.search}
                onChange={(e) => setLocalFilters({ ...localFilters, search: e.target.value })}
              />
            </div>
            <div>
              <Label>Département</Label>
              <Select 
                value={localFilters.department} 
                onValueChange={(value) => setLocalFilters({ ...localFilters, department: value === 'all' ? 'all' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les départements" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les départements</SelectItem>
                  {getUniqueValues('department').map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status Action</Label>
              <Select 
                value={localFilters.status} 
                onValueChange={(value) => setLocalFilters({ ...localFilters, status: value === 'all' ? 'all' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les status</SelectItem>
                  <SelectItem value="Non Démarré">Non Démarré</SelectItem>
                  <SelectItem value="A l'étude">A l'étude</SelectItem>
                  <SelectItem value="Planifié">Planifié</SelectItem>
                  <SelectItem value="En Cours">En Cours</SelectItem>
                  <SelectItem value="Bloqué">Bloqué</SelectItem>
                  <SelectItem value="Abandonné">Abandonné</SelectItem>
                  <SelectItem value="Terminé">Terminé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => setLocalFilters({ department: 'all', status: 'all', search: '' })}
                className="w-full"
              >
                Réinitialiser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions List */}
      {filteredActions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune Action</h3>
            <p className="text-gray-600">
              {actions.length === 0 
                ? "Vous n'avez aucune action assignée pour le moment."
                : "Aucune action ne correspond à vos critères de recherche."
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredActions.map((action) => (
            <Card key={action.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">{action.title}</h3>
                          <StatusBadge status={action.status} />
                        </div>
                        
                        <p className="text-gray-600 mb-4 line-clamp-2">{action.description}</p>
                        
                        {/* Progress Bar */}
                        {(action.step_count || 0) > 0 && (
                          <div className="mb-4">
                            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                              <span>Progression des étapes</span>
                              <span className="font-medium">{action.steps_progress || 0}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  (action.steps_progress || 0) === 100 
                                    ? 'bg-green-500' 
                                    : (action.steps_progress || 0) > 0 
                                      ? 'bg-blue-500' 
                                      : 'bg-gray-300'
                                }`}
                                style={{ width: `${action.steps_progress || 0}%` }}
                              ></div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {Math.round((action.steps_progress || 0) / 100 * (action.step_count || 0))} / {action.step_count || 0} étapes terminées
                            </div>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <ClipboardList className="w-4 h-4" />
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              (action.step_count || 0) > 0 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {action.step_count || 0} étape{(action.step_count || 0) !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {action.department && (
                            <div className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              <span>{action.department}</span>
                            </div>
                          )}
                          {action.participants && action.participants.length > 0 && (
                            <div className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              <span>{getUsersByIds(action.participants).map(u => u.name).join(', ')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleViewAction(action)} 
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Voir cette action"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleEditAction(action)} 
                      className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                      title="Modifier cette action"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Action Dialog */}
      {selectedAction && (
        <EditActionDialog 
          action={selectedAction}
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onActionUpdated={handleActionUpdated}
          users={users}
        />
      )}
    </div>
  );
};

const MesActivites = ({ filters: externalFilters = {} }) => {
  const [steps, setSteps] = useState([]);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredSteps, setFilteredSteps] = useState([]);
  const [localFilters, setLocalFilters] = useState({
    search: '',
    dateFilter: 'all', // 'all', 'upcoming', 'current', 'overdue'
    statusFilter: externalFilters.statusFilter || 'all'
  });
  const [selectedStep, setSelectedStep] = useState(null);
  const [editStepOpen, setEditStepOpen] = useState(false);

  useEffect(() => {
    fetchMySteps();
    fetchMyActions();
    fetchUsers();
  }, []);

  useEffect(() => {
    // Handle external filters
    if (externalFilters.statusFilter) {
      setLocalFilters(prev => ({ ...prev, statusFilter: externalFilters.statusFilter }));
    }
  }, [externalFilters]);

  useEffect(() => {
    applyFilters();
  }, [steps, localFilters]);

  const fetchMySteps = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users/me/steps`);
      setSteps(response.data);
    } catch (error) {
      console.error('Error fetching my steps:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyActions = async () => {
    try {
      const response = await axios.get(`${API}/users/me/actions`);
      setActions(response.data);
    } catch (error) {
      console.error('Error fetching my actions:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...steps];
    const currentDate = new Date();

    // Apply search filter
    if (localFilters.search) {
      const searchLower = localFilters.search.toLowerCase();
      filtered = filtered.filter(step => 
        step.title.toLowerCase().includes(searchLower) ||
        (step.description && step.description.toLowerCase().includes(searchLower))
      );
    }

    // Apply date filter
    if (localFilters.dateFilter !== 'all') {
      filtered = filtered.filter(step => {
        const startDate = step.start_date ? new Date(step.start_date) : null;
        const endDate = step.end_date ? new Date(step.end_date) : null;

        switch (localFilters.dateFilter) {
          case 'upcoming':
            return startDate && startDate > currentDate;
          case 'current':
            return startDate && startDate <= currentDate && (!endDate || endDate >= currentDate);
          case 'overdue':
            return endDate && endDate < currentDate;
          default:
            return true;
        }
      });
    }

    // Apply status filter (based on step status derived from dates)
    if (localFilters.statusFilter !== 'all') {
      filtered = filtered.filter(step => {
        const stepStatus = getStepStatus(step);
        return stepStatus.status === localFilters.statusFilter;
      });
    }

    setFilteredSteps(filtered);
  };

  const getActionTitle = (actionId) => {
    const action = actions.find(a => a.id === actionId);
    return action ? action.title : 'Action inconnue';
  };

  const getUsersByIds = (userIds) => {
    return users.filter(user => userIds.includes(user.id));
  };

  const calculateDaysDifference = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStepStatus = (step) => {
    const currentDate = new Date();
    const startDate = step.start_date ? new Date(step.start_date) : null;
    const endDate = step.end_date ? new Date(step.end_date) : null;

    if (!startDate) return { status: 'À faire', color: 'bg-gray-100 text-gray-800' };
    if (endDate && endDate < currentDate) return { status: 'En retard', color: 'bg-red-100 text-red-800' };
    if (startDate <= currentDate) return { status: 'En cours', color: 'bg-blue-100 text-blue-800' };
    return { status: 'À venir', color: 'bg-yellow-100 text-yellow-800' };
  };

  const handleViewStep = (step) => {
    setSelectedStep(step);
    setEditStepOpen(true);
  };

  const handleStepUpdated = (updatedStep) => {
    setSteps(steps.map(step => step.id === updatedStep.id ? updatedStep : step));
    setEditStepOpen(false);
    setSelectedStep(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mes Activités</h1>
          <p className="text-gray-600 mt-1">Toutes les étapes assignées dans vos projets</p>
        </div>
        <div className="text-sm text-gray-600">
          {filteredSteps.length} étape(s) sur {steps.length}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Rechercher</Label>
              <Input
                placeholder="Rechercher une étape..."
                value={localFilters.search}
                onChange={(e) => setLocalFilters({ ...localFilters, search: e.target.value })}
              />
            </div>
            <div>
              <Label>Filtrer par période</Label>
              <Select 
                value={localFilters.dateFilter} 
                onValueChange={(value) => setLocalFilters({ ...localFilters, dateFilter: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les périodes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les périodes</SelectItem>
                  <SelectItem value="current">En cours</SelectItem>
                  <SelectItem value="upcoming">À venir</SelectItem>
                  <SelectItem value="overdue">En retard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => setLocalFilters({ search: '', dateFilter: 'all', statusFilter: 'all' })}
                className="w-full"
              >
                Réinitialiser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Steps List */}
      {filteredSteps.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ClipboardList className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune Étape</h3>
            <p className="text-gray-600">
              {steps.length === 0 
                ? "Vous n'avez aucune étape assignée pour le moment."
                : "Aucune étape ne correspond à vos critères de recherche."
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredSteps.map((step, index) => {
            const stepStatus = getStepStatus(step);
            const stepParticipants = getUsersByIds(step.participants || []);
            
            return (
              <Card key={step.id} className="border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                          <p className="text-sm text-gray-600">Action: {getActionTitle(step.action_id)}</p>
                        </div>
                        <Badge className={stepStatus.color}>
                          {stepStatus.status}
                        </Badge>
                      </div>

                      {step.description && (
                        <p className="text-gray-600 text-sm mb-4 ml-11">{step.description}</p>
                      )}

                      <div className="ml-11 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <Label className="text-xs font-medium text-gray-600">Date de Début</Label>
                          <p className="mt-1">
                            {step.start_date ? new Date(step.start_date).toLocaleDateString('fr-FR') : 'Non définie'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-gray-600">Date de Fin</Label>
                          <p className="mt-1">
                            {step.end_date ? new Date(step.end_date).toLocaleDateString('fr-FR') : 'Non définie'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-gray-600">Durée</Label>
                          <p className="mt-1">
                            {calculateDaysDifference(step.start_date, step.end_date) 
                              ? `${calculateDaysDifference(step.start_date, step.end_date)} jour(s)`
                              : 'Non calculée'
                            }
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-gray-600">Participants</Label>
                          <div className="mt-1">
                            {stepParticipants.length > 0 ? (
                              <div className="text-xs">
                                {stepParticipants.length > 2 ? (
                                  <>
                                    {stepParticipants.slice(0, 2).map(user => user.name).join(', ')}
                                    <span className="text-gray-500"> +{stepParticipants.length - 2}</span>
                                  </>
                                ) : (
                                  stepParticipants.map(user => user.name).join(', ')
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">Aucun participant</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-4">
                      <button
                        onClick={() => handleViewStep(step)}
                        className="inline-flex items-center justify-center w-10 h-10 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:text-blue-700 transition-colors"
                        title="En savoir plus"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Step Dialog */}
      {selectedStep && (
        <EditStepDialog 
          step={selectedStep}
          open={editStepOpen}
          onOpenChange={setEditStepOpen}
          onStepUpdated={handleStepUpdated}
          users={users}
        />
      )}
    </div>
  );
};

// Default PST Dialog Component
const DefaultPSTDialog = ({ user, plans: parentPlans, selectedPlan, setSelectedPlan }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [defaultPst, setDefaultPst] = useState(null);
  const [selectedPst, setSelectedPst] = useState('');

  useEffect(() => {
    if (open) {
      // Use parent plans if available, otherwise fetch them
      if (parentPlans && parentPlans.length > 0) {
        setPlans(parentPlans);
      } else {
        fetchPlans();
      }
      fetchDefaultPst();
    }
  }, [open, parentPlans]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchDefaultPst = async () => {
    try {
      const response = await axios.get(`${API}/users/me/default-pst`);
      if (response.data.default_pst) {
        setDefaultPst(response.data.plan);
        setSelectedPst(response.data.default_pst);
      }
    } catch (error) {
      console.error('Error fetching default PST:', error);
    }
  };

  const handleSetDefault = async () => {
    if (!selectedPst) return;

    setLoading(true);
    try {
      await axios.put(`${API}/users/me/default-pst?plan_id=${selectedPst}`);
      await fetchDefaultPst();
      
      // Update parent component's selectedPlan if available
      if (setSelectedPlan && plans) {
        const newDefaultPlan = plans.find(plan => plan.id === selectedPst);
        if (newDefaultPlan) {
          setSelectedPlan(newDefaultPlan);
        }
      }
      
      setOpen(false);
    } catch (error) {
      console.error('Error setting default PST:', error);
      alert('Erreur lors de la définition du PST par défaut');
    } finally {
      setLoading(false);
    }
  };

  const handleClearDefault = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/users/me/default-pst`);
      setDefaultPst(null);
      setSelectedPst('');
      setOpen(false);
    } catch (error) {
      console.error('Error clearing default PST:', error);
      alert('Erreur lors de la suppression du PST par défaut');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="w-full justify-start text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 p-2 h-auto"
        title={defaultPst ? `PST par défaut: ${defaultPst.name}` : 'Définir PST par défaut'}
      >
        <Settings className="h-3 w-3 mr-2 flex-shrink-0" />
        <span className="truncate">
          {defaultPst ? `PST par défaut: ${defaultPst.name}` : 'Définir PST par défaut'}
        </span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>PST par Défaut</DialogTitle>
            <p className="text-sm text-gray-600">
              Définissez un PST par défaut qui sera automatiquement sélectionné lors de votre connexion.
            </p>
          </DialogHeader>
          <div className="space-y-4 p-1">
            {defaultPst && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm font-medium text-blue-900">PST actuel par défaut:</p>
                <p className="text-sm text-blue-700 break-words">{defaultPst.name}</p>
              </div>
            )}

            <div>
              <Label htmlFor="pst-select">Choisir un nouveau PST par défaut</Label>
              <Select value={selectedPst} onValueChange={setSelectedPst}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Sélectionner un PST" />
                </SelectTrigger>
                <SelectContent className="max-w-[400px]">
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id} className="break-words">
                      {plan.name} ({plan.start_year}-{plan.end_year})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col space-y-3 pt-2">
              {defaultPst && (
                <Button
                  variant="outline"
                  onClick={handleClearDefault}
                  disabled={loading}
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                >
                  Supprimer par défaut
                </Button>
              )}
              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setOpen(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSetDefault}
                  disabled={!selectedPst || loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? 'Sauvegarde...' : 'Définir par défaut'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Gérer les PSTs Component with Submenu
const GererPSTs = ({ plans, onPlanCreated, onPlanUpdated, onPlanDeleted }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get sub-tab from URL for PST management
  const getSubTabFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('subtab') || 'gestion';
  };
  
  const [activeTab, setActiveTab] = useState(getSubTabFromURL());
  const [deletedPlans, setDeletedPlans] = useState([]);
  const [archivedPlans, setArchivedPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  // Update URL when subtab changes
  const handleSubTabChange = (subtab) => {
    setActiveTab(subtab);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('subtab', subtab);
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  // Listen for URL changes
  useEffect(() => {
    const newSubTab = getSubTabFromURL();
    if (newSubTab !== activeTab) {
      setActiveTab(newSubTab);
    }
  }, [location.search]);

  useEffect(() => {
    if (activeTab === 'supprime') {
      fetchDeletedPlans();
    } else if (activeTab === 'archives') {
      fetchArchivedPlans();
    }
  }, [activeTab]);

  const fetchDeletedPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/plans/deleted`);
      setDeletedPlans(response.data);
    } catch (error) {
      console.error('Error fetching deleted plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/plans/archived`);
      setArchivedPlans(response.data);
    } catch (error) {
      console.error('Error fetching archived plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'gestion', label: 'Gestion', icon: Settings },
    { id: 'supprime', label: 'PST supprimé', icon: Trash2 },
    { id: 'archives', label: 'Archives', icon: Archive }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
          <Settings className="h-5 w-5 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Gérer les PSTs</h2>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleSubTabChange(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'gestion' && (
        <GestionTab 
          plans={plans} 
          onPlanCreated={onPlanCreated} 
          onPlanUpdated={onPlanUpdated} 
          onPlanDeleted={onPlanDeleted}
        />
      )}
      {activeTab === 'supprime' && (
        <PSTSupprimeTab 
          deletedPlans={deletedPlans}
          onRefresh={fetchDeletedPlans}
          loading={loading}
        />
      )}
      {activeTab === 'archives' && (
        <ArchivesTab 
          archivedPlans={archivedPlans}
          onRefresh={fetchArchivedPlans}
          loading={loading}
        />
      )}
    </div>
  );
};

// Gestion Tab Component (Current Gérer les PSTs functionality + Archive button)
const GestionTab = ({ plans, onPlanCreated, onPlanUpdated, onPlanDeleted }) => {
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
    setLoading(false);
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlan) return;
    
    try {
      await axios.delete(`${API}/plans/${selectedPlan.id}`);
      alert('PST supprimé avec succès (peut être récupéré)');
      onPlanDeleted(); // Call the proper callback for deletion
      setDeleteDialogOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Erreur lors de la suppression du PST');
    }
  };

  const handleArchivePlan = async () => {
    if (!selectedPlan) return;
    
    try {
      await axios.post(`${API}/plans/${selectedPlan.id}/archive`);
      alert('PST archivé avec succès');
      onPlanDeleted(); // Refresh the plans list
      setArchiveDialogOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Error archiving plan:', error);
      alert('Erreur lors de l\'archivage du PST');
    }
  };

  const handleAssignPlan = async (userId, planId, assigned) => {
    try {
      const user = users.find(u => u.id === userId);
      const currentAccess = user.pst_access || [];
      
      let newAccess;
      if (assigned) {
        newAccess = [...currentAccess, planId];
      } else {
        newAccess = currentAccess.filter(id => id !== planId);
      }

      await axios.put(`${API}/admin/users/${userId}`, {
        pst_access: newAccess
      });
      
      fetchUsers(); // Refresh users list
      alert('Assignation mise à jour avec succès');
    } catch (error) {
      console.error('Error updating plan assignment:', error);
      alert('Erreur lors de la mise à jour de l\'assignation');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <Settings className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Gérer les PSTs</h2>
        </div>
        <CreatePlanDialog onPlanCreated={onPlanCreated} />
      </div>

      {/* PSTs List */}
      <Card>
        <CardHeader>
          <CardTitle>Plans Stratégiques Transversaux</CardTitle>
        </CardHeader>
        <CardContent>
          {plans.length === 0 ? (
            <div className="text-center py-8">
              <Flag className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun PST créé</p>
            </div>
          ) : (
            <div className="space-y-4">
              {plans.map((plan) => (
                <div key={plan.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{plan.name}</h3>
                      <p className="text-gray-600">{plan.start_year} - {plan.end_year}</p>
                      {plan.description && (
                        <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedPlan(plan);
                          setEditDialogOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Modifier
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedPlan(plan);
                          setAssignDialogOpen(true);
                        }}
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Assigner
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedPlan(plan);
                          setArchiveDialogOpen(true);
                        }}
                        className="text-orange-600 hover:text-orange-700"
                        title="Archiver ce PST"
                      >
                        <Archive className="h-4 w-4 mr-1" />
                        Archiver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedPlan(plan);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Plan Dialog */}
      {selectedPlan && (
        <EditPlanDialog
          plan={selectedPlan}
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedPlan(null);
          }}
          onPlanUpdated={onPlanUpdated}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Êtes-vous sûr de vouloir supprimer le PST "{selectedPlan?.name}" ?</p>
            <p className="text-sm text-red-600">Cette action supprime le PST mais il peut être récupéré depuis l'onglet "PST supprimé".</p>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Annuler
              </Button>
              <Button variant="destructive" onClick={handleDeletePlan}>
                Supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Plan Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assigner le PST "{selectedPlan?.name}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              {users.map((user) => {
                const hasAccess = user.pst_access?.includes(selectedPlan?.id) || false;
                return (
                  <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-gray-600">{user.email} - {user.role}</p>
                      <p className="text-xs text-gray-500">{user.department}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm ${hasAccess ? 'text-green-600' : 'text-gray-500'}`}>
                        {hasAccess ? 'Assigné' : 'Non assigné'}
                      </span>
                      <Button
                        size="sm"
                        variant={hasAccess ? "destructive" : "default"}
                        onClick={() => handleAssignPlan(user.id, selectedPlan.id, !hasAccess)}
                      >
                        {hasAccess ? 'Retirer' : 'Assigner'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer l'archivage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Êtes-vous sûr de vouloir archiver le PST "{selectedPlan?.name}" ?</p>
            <p className="text-sm text-orange-600">Le PST sera archivé et pourra être récupéré depuis l'onglet Archives.</p>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleArchivePlan} className="bg-orange-600 hover:bg-orange-700">
                Archiver
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// PST Supprimé Tab Component
const PSTSupprimeTab = ({ deletedPlans, onRefresh, loading }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [recoverDialogOpen, setRecoverDialogOpen] = useState(false);

  const handleRecoverPlan = async () => {
    if (!selectedPlan) return;
    
    try {
      await axios.post(`${API}/plans/${selectedPlan.id}/recover`);
      alert('PST récupéré avec succès');
      onRefresh(); // Refresh the deleted plans list
      setRecoverDialogOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Error recovering plan:', error);
      alert('Erreur lors de la récupération du PST');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
            <Trash2 className="h-5 w-5 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">PSTs Supprimés</h3>
        </div>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Deleted PSTs List */}
      <Card>
        <CardHeader>
          <CardTitle>Plans Stratégiques Supprimés</CardTitle>
        </CardHeader>
        <CardContent>
          {deletedPlans.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun PST supprimé</p>
            </div>
          ) : (
            <div className="space-y-4">
              {deletedPlans.map((plan) => (
                <div key={plan.id} className="border rounded-lg p-4 bg-red-50 border-red-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{plan.name}</h3>
                      <p className="text-gray-600">{plan.start_year} - {plan.end_year}</p>
                      {plan.description && (
                        <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                      )}
                      <div className="text-xs text-red-600 mt-2">
                        Supprimé le {new Date(plan.deleted_at).toLocaleDateString('fr-FR')} 
                        {plan.deleted_by && ` par ${plan.deleted_by}`}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => {
                          setSelectedPlan(plan);
                          setRecoverDialogOpen(true);
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Récupérer
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recover Confirmation Dialog */}
      <Dialog open={recoverDialogOpen} onOpenChange={setRecoverDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la récupération</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Êtes-vous sûr de vouloir récupérer le PST "{selectedPlan?.name}" ?</p>
            <p className="text-sm text-green-600">Le PST sera restauré et redeviendra accessible.</p>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setRecoverDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleRecoverPlan} className="bg-green-600 hover:bg-green-700">
                Récupérer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Archives Tab Component
const ArchivesTab = ({ archivedPlans, onRefresh, loading }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);

  const handleUnarchivePlan = async () => {
    if (!selectedPlan) return;
    
    try {
      await axios.post(`${API}/plans/${selectedPlan.id}/unarchive`);
      alert('PST désarchivé avec succès');
      onRefresh(); // Refresh the archived plans list
      setUnarchiveDialogOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Error unarchiving plan:', error);
      alert('Erreur lors du désarchivage du PST');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <Archive className="h-5 w-5 text-gray-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">PSTs Archivés</h3>
        </div>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Archived PSTs List */}
      <Card>
        <CardHeader>
          <CardTitle>Plans Stratégiques Archivés</CardTitle>
        </CardHeader>
        <CardContent>
          {archivedPlans.length === 0 ? (
            <div className="text-center py-8">
              <Archive className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun PST archivé</p>
            </div>
          ) : (
            <div className="space-y-4">
              {archivedPlans.map((plan) => (
                <div key={plan.id} className="border rounded-lg p-4 bg-gray-50 border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-800">{plan.name}</h3>
                      <p className="text-gray-600">{plan.start_year} - {plan.end_year}</p>
                      {plan.description && (
                        <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                      )}
                      <div className="text-xs text-gray-600 mt-2">
                        Archivé le {new Date(plan.archived_at).toLocaleDateString('fr-FR')}
                        {plan.archived_by && ` par ${plan.archived_by}`}
                      </div>
                      <Badge variant="secondary" className="mt-2">Lecture seule</Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedPlan(plan);
                          setUnarchiveDialogOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Désarchiver
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unarchive Confirmation Dialog */}
      <Dialog open={unarchiveDialogOpen} onOpenChange={setUnarchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer le désarchivage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Êtes-vous sûr de vouloir désarchiver le PST "{selectedPlan?.name}" ?</p>
            <p className="text-sm text-blue-600">Le PST sera réactivé et redeviendra modifiable.</p>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setUnarchiveDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleUnarchivePlan} className="bg-blue-600 hover:bg-blue-700">
                Désarchiver
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Documents Component with Sub-tabs
const Documents = () => {
  const [activeSubTab, setActiveSubTab] = useState('rapports');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
          <FileText className="h-5 w-5 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Documents</h2>
      </div>

      {/* Sub-navigation */}
      <div className="border-b">
        <nav className="flex space-x-8">
          {[
            { id: 'rapports', label: 'Rapports' },
            { id: 'modeles', label: 'Modèles' },
            { id: 'tutoriel', label: 'Tutoriel' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeSubTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Sub-tab Content */}
      {activeSubTab === 'rapports' && <RapportsTab />}
      {activeSubTab === 'modeles' && <ModelesTab />}
      {activeSubTab === 'tutoriel' && <TutorielTab />}
    </div>
  );
};

// Rapports Tab Component
const RapportsTab = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReports, setSelectedReports] = useState([]);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (report) => {
    try {
      const response = await axios.get(`${API}/reports/${report.id}`, {
        responseType: 'blob'
      });

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.name}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Erreur lors du téléchargement du rapport');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) return;
    
    try {
      await axios.delete(`${API}/reports/${reportId}`);
      setReports(reports.filter(r => r.id !== reportId));
      alert('Rapport supprimé avec succès');
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Erreur lors de la suppression du rapport');
    }
  };

  const filteredReports = reports.filter(report =>
    report.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleReportSelection = (reportId) => {
    setSelectedReports(prev => 
      prev.includes(reportId)
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleDeleteReports = async () => {
    if (selectedReports.length === 0) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedReports.length} rapport(s) ?`)) {
      try {
        // Mock deletion - replace with actual API calls
        setReports(prev => prev.filter(report => !selectedReports.includes(report.id)));
        setSelectedReports([]);
        alert('Rapports supprimés avec succès');
      } catch (error) {
        console.error('Error deleting reports:', error);
        alert('Erreur lors de la suppression des rapports');
      }
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement des rapports...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Search and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Rechercher un rapport..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {selectedReports.length > 0 && (
          <Button
            variant="destructive"
            onClick={handleDeleteReports}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Supprimer ({selectedReports.length})
          </Button>
        )}
      </div>

      {/* Reports Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={selectedReports.length === filteredReports.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedReports(filteredReports.map(r => r.id));
                      } else {
                        setSelectedReports([]);
                      }
                    }}
                  />
                </TableHead>
                <TableHead>Nom du rapport</TableHead>
                <TableHead>Extension</TableHead>
                <TableHead>Date de création</TableHead>
                <TableHead>Taille</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedReports.includes(report.id)}
                      onChange={() => toggleReportSelection(report.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{report.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">DOCX</Badge>
                  </TableCell>
                  <TableCell>{new Date(report.generated_at).toLocaleDateString('fr-FR')}</TableCell>
                  <TableCell>{(report.file_size / 1024 / 1024).toFixed(2)} MB</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadReport(report)}
                      >
                        Télécharger
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteReport(report.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredReports.length === 0 && (
            <div className="text-center py-8">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {searchTerm ? 'Aucun rapport trouvé' : 'Aucun rapport généré'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Modèles Tab Component
const ModelesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
    fetchPlans();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/documents/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleGenerateDocument = (template) => {
    setSelectedTemplate(template);
    setGenerateDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    alert(`Modification du modèle: ${template.name} (fonctionnalité à venir)`);
  };

  if (loading) {
    return <div className="text-center py-8">Chargement des modèles...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Search and Create */}
      <div className="flex items-center justify-between">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Rechercher un modèle..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <Card key={template.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{template.name}</CardTitle>
                <Badge variant="outline">{template.format}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">{template.description}</p>
              <p className="text-xs text-gray-500 mb-4">
                Format: {template.format}
              </p>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEditTemplate(template)}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Modifier
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleGenerateDocument(template)}
                  className="flex-1"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Générer
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchTerm ? 'Aucun modèle trouvé' : 'Aucun modèle disponible'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Generate Document Dialog */}
      <GenerateDocumentDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        template={selectedTemplate}
        plans={plans}
      />
    </div>
  );
};

// Generate Document Dialog Component
const GenerateDocumentDialog = ({ open, onClose, template, plans }) => {
  const [step, setStep] = useState(1); // 1: Select PST, 2: Select Objective (if needed)
  const [selectedPlan, setSelectedPlan] = useState('');
  const [strategicObjectives, setStrategicObjectives] = useState([]);
  const [operationalObjectives, setOperationalObjectives] = useState([]);
  const [selectedObjective, setSelectedObjective] = useState('');
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedPlan('');
      setSelectedObjective('');
      setStrategicObjectives([]);
      setOperationalObjectives([]);
    }
  }, [open]);

  const handlePlanSelection = async () => {
    if (!selectedPlan || !template) return;

    // For rapport-pst, proceed directly to generation
    if (template.selection_type === 'pst_only') {
      await handleGenerate();
      return;
    }

    // For objective-based templates, fetch objectives
    setLoading(true);
    try {
      if (template.selection_type === 'strategic_objective') {
        const response = await axios.get(`${API}/documents/strategic-objectives/${selectedPlan}`);
        setStrategicObjectives(response.data);
      } else if (template.selection_type === 'operational_objective') {
        const response = await axios.get(`${API}/documents/operational-objectives/${selectedPlan}`);
        setOperationalObjectives(response.data);
      }
      setStep(2);
    } catch (error) {
      console.error('Error fetching objectives:', error);
      alert('Erreur lors de la récupération des objectifs');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedPlan || !template) return;
    
    // For objective-based templates, ensure objective is selected
    if (template.selection_type !== 'pst_only' && !selectedObjective) return;

    setGenerating(true);
    try {
      let endpoint = '';
      if (template.id === 'rapport-pst') {
        endpoint = `/documents/generate/rapport-pst/${selectedPlan}`;
      } else if (template.id === 'chart-objectifs-strategiques') {
        endpoint = `/documents/generate/chart-objectifs-strategiques/${selectedObjective}`;
      } else if (template.id === 'chart-objectifs-operationnels') {
        endpoint = `/documents/generate/chart-objectifs-operationnels/${selectedObjective}`;
      }

      if (endpoint) {
        const response = await axios.get(`${API}${endpoint}`, {
          responseType: 'blob'
        });

        // Extract filename from response headers
        const contentDisposition = response.headers['content-disposition'];
        let filename = `${template.name}.docx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename=(.+)/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        // Create blob URL and trigger download
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        alert('Document généré avec succès !');
        onClose();
      }
    } catch (error) {
      console.error('Error generating document:', error);
      alert('Erreur lors de la génération du document');
    } finally {
      setGenerating(false);
    }
  };

  const getStepTitle = () => {
    if (step === 1) return 'Sélectionner un PST';
    if (template.selection_type === 'strategic_objective') return 'Sélectionner un Objectif Stratégique';
    if (template.selection_type === 'operational_objective') return 'Sélectionner un Objectif Opérationnel';
    return '';
  };

  const getObjectivesList = () => {
    if (template.selection_type === 'strategic_objective') return strategicObjectives;
    if (template.selection_type === 'operational_objective') return operationalObjectives;
    return [];
  };

  const renderObjectiveOption = (objective) => {
    return (
      <SelectItem key={objective.id} value={objective.id} className="py-2 max-w-full">
        <div className="flex flex-col w-full overflow-hidden">
          <span className="font-medium text-sm truncate">{objective.code} - {objective.title}</span>
          <span className="text-xs text-gray-500 truncate">
            {template.selection_type === 'strategic_objective' 
              ? `${objective.vision_title} → ${objective.panel_title}`
              : `${objective.vision_title} → ${objective.panel_title} → ${objective.strategic_objective_code}`
            }
          </span>
        </div>
      </SelectItem>
    );
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Générer {template.name}</DialogTitle>
          <p className="text-sm text-gray-600">{template.description}</p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step indicator */}
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span className={`flex items-center ${step === 1 ? 'text-blue-600 font-medium' : ''}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs mr-2 ${
                step === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>1</span>
              PST
            </span>
            {template.selection_type !== 'pst_only' && (
              <>
                <ChevronRight className="h-4 w-4" />
                <span className={`flex items-center ${step === 2 ? 'text-blue-600 font-medium' : ''}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs mr-2 ${
                    step === 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>2</span>
                  {template.selection_type === 'strategic_objective' ? 'O.S.' : 'O.O.'}
                </span>
              </>
            )}
          </div>

          {/* Step 1: Select PST */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="plan-select">{getStepTitle()}</Label>
                <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un PST..." />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} ({plan.start_year}-{plan.end_year})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={onClose}>
                  Annuler
                </Button>
                <Button 
                  onClick={handlePlanSelection} 
                  disabled={!selectedPlan || loading}
                >
                  {loading ? 'Chargement...' : 
                   template.selection_type === 'pst_only' ? 'Générer et Télécharger' : 'Suivant'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Select Objective */}
          {step === 2 && (
            <div className="space-y-4">
                <div className="w-full">
                  <Label htmlFor="objective-select">{getStepTitle()}</Label>
                  <Select value={selectedObjective} onValueChange={setSelectedObjective}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={`Choisir un ${template.selection_type === 'strategic_objective' ? 'objectif stratégique' : 'objectif opérationnel'}...`} />
                    </SelectTrigger>
                    <SelectContent 
                      className="max-h-[250px] overflow-y-auto z-50" 
                      position="popper"
                      side="bottom"
                      align="start"
                      avoidCollisions={true}
                      collisionBoundary={document.body}
                      sticky="always"
                    >
                      {getObjectivesList().map(renderObjectiveOption)}
                    </SelectContent>
                  </Select>
                </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setStep(1)}>
                  Retour
                </Button>
                <Button variant="outline" onClick={onClose}>
                  Annuler
                </Button>
                <Button 
                  onClick={handleGenerate} 
                  disabled={!selectedObjective || generating}
                >
                  {generating ? 'Génération...' : 'Générer et Télécharger'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Create Model Dialog Component
const CreateModelDialog = ({ open, onClose, onModelCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'Rapport',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Mock creation - replace with actual API call
      alert('Modèle créé avec succès');
      onModelCreated();
      onClose();
      setFormData({ name: '', type: 'Rapport', description: '' });
    } catch (error) {
      console.error('Error creating model:', error);
      alert('Erreur lors de la création du modèle');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Créer un nouveau modèle</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="model-name">Nom du modèle</Label>
            <Input
              id="model-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="model-type">Type</Label>
            <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Rapport">Rapport</SelectItem>
                <SelectItem value="Présentation">Présentation</SelectItem>
                <SelectItem value="Formulaire">Formulaire</SelectItem>
                <SelectItem value="Tableau">Tableau</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="model-description">Description</Label>
            <Textarea
              id="model-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit">
              Créer
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Tutoriel Tab Component
const TutorielTab = () => {
  const tutorials = [
    {
      title: "Créer un rapport personnalisé",
      steps: [
        "Accédez à l'onglet 'Modèles'",
        "Cliquez sur 'Créer un modèle'",
        "Sélectionnez le type 'Rapport'",
        "Définissez la structure et les champs requis",
        "Sauvegardez votre modèle"
      ]
    },
    {
      title: "Générer un document à partir d'un modèle",
      steps: [
        "Sélectionnez le modèle souhaité dans l'onglet 'Modèles'",
        "Cliquez sur 'Générer'",
        "Remplissez les champs requis",
        "Prévisualisez le document",
        "Téléchargez ou sauvegardez le document généré"
      ]
    },
    {
      title: "Gérer les rapports existants",
      steps: [
        "Accédez à l'onglet 'Rapports'",
        "Utilisez la barre de recherche pour trouver un rapport",
        "Sélectionnez les rapports à gérer",
        "Utilisez les actions disponibles (télécharger, supprimer)",
        "Confirmez vos actions si nécessaire"
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Guide d'utilisation des documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-6">
            Ce guide vous explique comment créer, gérer et utiliser les documents et modèles dans le système PST.
          </p>
          
          <div className="space-y-8">
            {tutorials.map((tutorial, index) => (
              <div key={index} className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-lg font-semibold mb-3">{tutorial.title}</h3>
                <ol className="space-y-2">
                  {tutorial.steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="flex items-start">
                      <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-white bg-blue-500 rounded-full mr-3 mt-0.5">
                        {stepIndex + 1}
                      </span>
                      <span className="text-gray-700">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
          
          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">💡 Conseils utiles</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Utilisez des noms descriptifs pour vos modèles</li>
              <li>• Organisez vos rapports par date ou par projet</li>
              <li>• Sauvegardez régulièrement vos modèles personnalisés</li>
              <li>• Vérifiez les permissions avant de partager des documents</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default App;
